import React, { useState, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import "./RenamePage.css";
import { API_BASE } from "../api/config";

const DEFAULT_API = API_BASE;
const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN || window.__API_ORIGIN__ || DEFAULT_API).replace(/\/+$/, "");
const PROCESS_URL_V1 = `${API_ORIGIN}/rename-me-ocr/api/process`;
const PROCESS_URL_V2 = `${API_ORIGIN}/rename-me-v2/api/process`;
const BATCH_START_URL = `${API_ORIGIN}/rename-me-ocr/api/batch/start`;
const BATCH_PROGRESS_URL = `${API_ORIGIN}/rename-me-ocr/api/batch/progress`;
const BATCH_STOP_URL = `${API_ORIGIN}/rename-me-ocr/api/batch/stop`;
const BATCH_ZIP_URL = `${API_ORIGIN}/rename-me-ocr/api/batch/zip`;
const DROPBOX_RUN_ONCE_URL = `${API_ORIGIN}/rename-me-ocr/api/dropbox/run-once`;
const DROPBOX_STATS_URL = `${API_ORIGIN}/rename-me-ocr/api/dropbox/stats`;
const DUPLICATES_CREATE_URL = `${API_ORIGIN}/rename-me-ocr/api/duplicates/create`;
const BWA_EXTRACT_URL = `${API_ORIGIN}/rename-me-ocr/api/bwa/extract-ustva`;
const N26_SPLIT_URL = `${API_ORIGIN}/rename-me-ocr/api/n26/split`;
const EVIDENCE_SAVE_CORRECTION_URL = `${API_ORIGIN}/rename-me-ocr/api/evidence/save-correction`;
const REQUEST_TIMEOUT_MS = 90000; // 45s – Safari wird sonst gern ungeduldig
const MAX_UPLOAD_MB = parseInt(process.env.REACT_APP_MAX_UPLOAD_MB || "20", 10);

const SCHEMA_OPTIONS = [
  { value: "auto", label: "Automatisch" },
  { value: "kontoauszug", label: "Kontoauszug" },
  { value: "rechnung", label: "Rechnung" },
  { value: "gehaltsabrechnung", label: "Gehaltsabrechnung" },
  { value: "angebot/auftragsbestaetigung/lieferschein", label: "Angebot/AB/Lieferschein" },
  { value: "bwa/ustva", label: "BWA/UStVA" },
  { value: "gutschrift/mahnung", label: "Gutschrift/Mahnung" },
  { value: "unbekannt", label: "Unbekannt" },
];

// Default-Paths für TEST (aus Build-Env), werden als Fallback genutzt
const DBX_DEFAULTS = {
  inbox: process.env.REACT_APP_DROPBOX_INBOX_TEST || "",
  outbox: process.env.REACT_APP_DROPBOX_OUTBOX_TEST || "",
  processed: process.env.REACT_APP_DROPBOX_PROCESSED_TEST || "",
  error: process.env.REACT_APP_DROPBOX_ERROR_TEST || "",
};


// === DraggableResizableBox Komponente ===
// Ermöglicht das Verschieben und Vergrößern/Verkleinern von Evidence-Boxen
function DraggableResizableBox({
  kind,
  color,
  left,
  top,
  width,
  height,
  originalRect,
  isEditMode,
  isEditing,
  isModified,
  onSelect,
  onDragEnd,
  onResizeEnd,
}) {
  const boxRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  
  // Refs für aktuelle Position/Größe (werden synchron aktualisiert)
  const currentPosRef = useRef({ left, top });
  const currentSizeRef = useRef({ width, height });
  
  // State für Re-Render während Drag/Resize
  const [displayPos, setDisplayPos] = useState({ left, top });
  const [displaySize, setDisplaySize] = useState({ width, height });
  
  // Flag um zu tracken ob wir gerade manuell positioniert haben
  const justDraggedRef = useRef(false);

  // Sync mit Props wenn sie sich ändern (aber NUR wenn nicht gerade bearbeitet wird)
  useEffect(() => {
    if (!isDragging && !isResizing && !justDraggedRef.current) {
      currentPosRef.current = { left, top };
      currentSizeRef.current = { width, height };
      setDisplayPos({ left, top });
      setDisplaySize({ width, height });
    }
    // Reset das Flag nach einem Render-Cycle
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
    }
  }, [left, top, width, height, isDragging, isResizing]);

  // Drag-Handler
  const handleMouseDown = (e) => {
    if (!isEditMode) return;
    if (e.target.classList.contains('resize-handle')) return;
    
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: currentPosRef.current.left,
      top: currentPosRef.current.top,
    };
  };

  // Resize-Handler
  const handleResizeMouseDown = (e) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: currentSizeRef.current.width,
      height: currentSizeRef.current.height,
    };
  };

  // Global Mouse-Events während Drag/Resize
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      if (isDragging) {
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;
        const newPos = {
          left: dragStartRef.current.left + dx,
          top: dragStartRef.current.top + dy,
        };
        currentPosRef.current = newPos;
        setDisplayPos(newPos);
      }
      if (isResizing) {
        const dx = e.clientX - resizeStartRef.current.x;
        const dy = e.clientY - resizeStartRef.current.y;
        const newSize = {
          width: Math.max(20, resizeStartRef.current.width + dx),
          height: Math.max(20, resizeStartRef.current.height + dy),
        };
        currentSizeRef.current = newSize;
        setDisplaySize(newSize);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        // Speichere finale Position in Ref vor dem State-Update
        const finalPos = { ...currentPosRef.current };
        justDraggedRef.current = true;
        setIsDragging(false);
        // Callback mit den finalen Positionen aufrufen
        onDragEnd(finalPos.left, finalPos.top);
      }
      if (isResizing) {
        const finalSize = { ...currentSizeRef.current };
        justDraggedRef.current = true;
        setIsResizing(false);
        onResizeEnd(finalSize.width, finalSize.height);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, onDragEnd, onResizeEnd]);

  const solidColor = color.replace(/0\.\d+\)$/, '1)');
  const isSelected = isEditMode && isEditing;

  return (
    <div
      ref={boxRef}
      style={{
        position: 'absolute',
        left: displayPos.left,
        top: displayPos.top,
        width: displaySize.width,
        height: displaySize.height,
        boxSizing: 'border-box',
        border: `2px solid ${solidColor}`,
        background: isSelected ? color.replace(/0\.\d+\)$/, '0.4)') : color,
        borderRadius: 2,
        cursor: isEditMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
        outline: isModified ? '2px dashed orange' : (isSelected ? '2px solid white' : 'none'),
        outlineOffset: 2,
        zIndex: isSelected ? 10 : 1,
      }}
      title={`${kind}${isModified ? ' (geändert)' : ''}`}
      onMouseDown={handleMouseDown}
    >
      {/* Kind-Label */}
      {isEditMode && (
        <div style={{
          position: 'absolute',
          top: -20,
          left: 0,
          fontSize: 10,
          background: solidColor,
          color: 'white',
          padding: '1px 4px',
          borderRadius: 2,
          whiteSpace: 'nowrap',
        }}>
          {kind}{isModified ? ' ✎' : ''}
        </div>
      )}
      
      {/* Resize-Handle (unten rechts) */}
      {isEditMode && (
        <div
          className="resize-handle"
          style={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            width: 12,
            height: 12,
            background: solidColor,
            border: '2px solid white',
            borderRadius: 2,
            cursor: 'se-resize',
          }}
          onMouseDown={handleResizeMouseDown}
        />
      )}
    </div>
  );
}


export default function RenamePage() {
  const fileInputRef = useRef(null);
  // ===== Batch (Massenverarbeitung) – UI & State =====
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchSourceDir, setBatchSourceDir] = useState("");
  const [batchTargetDir, setBatchTargetDir] = useState(""); // Milestone 2
  const [batchErrorDir, setBatchErrorDir] = useState("");  // Milestone 2
  const [batchId, setBatchId] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchDone, setBatchDone] = useState(0);
  const [batchOk, setBatchOk] = useState(0);
  const [batchErr, setBatchErr] = useState(0);
  const [batchCurrent, setBatchCurrent] = useState("");
  const [batchLog, setBatchLog] = useState([]);
  const [batchError, setBatchError] = useState("");
  const batchPollRef = useRef(null);
  // ===== Batch – Directory Picker (Milestone 5) =====
  const [batchSourceHandle, setBatchSourceHandle] = useState(null);
  const [batchTargetHandle, setBatchTargetHandle] = useState(null);
  const [batchErrorHandle, setBatchErrorHandle] = useState(null);


  // ===== Dropbox Batch (wie HomePage) =====
  const [showDbxModal, setShowDbxModal] = useState(false);
  const [runningDbxBatch, setRunningDbxBatch] = useState(false);
  const [dbxBatchResult, setDbxBatchResult] = useState(null);
  const [dbxBatchError, setDbxBatchError] = useState("");
  const [dbxLiveFeed, setDbxLiveFeed] = useState([]);
  const [dbxFolderStats, setDbxFolderStats] = useState({ inbox: null, outbox: null, processed: null, error: null });
  const [dbxProgressPct, setDbxProgressPct] = useState(0);
  const [dbxBatchDone, setDbxBatchDone] = useState(false);
  const [dbxEditMode, setDbxEditMode] = useState(false);

  // Dropbox-Ordner (persistiert in localStorage)
  const [dbxInbox, setDbxInbox] = useState(localStorage.getItem("dbx_inbox") || DBX_DEFAULTS.inbox || "");
  const [dbxOutbox, setDbxOutbox] = useState(localStorage.getItem("dbx_outbox") || DBX_DEFAULTS.outbox || "");
  const [dbxProcessed, setDbxProcessed] = useState(localStorage.getItem("dbx_processed") || DBX_DEFAULTS.processed || "");
  const [dbxErrorFolder, setDbxErrorFolder] = useState(localStorage.getItem("dbx_error") || DBX_DEFAULTS.error || "");

  // ===== Batch – Browser-Mode control =====
  const [batchAbort, setBatchAbort] = useState(false);
  const browserModeActive = supportsDirPicker() && !!batchSourceHandle && !!batchTargetHandle && !!batchErrorHandle;
  // ===== Batch – Browser Mode (copy files locally via FS Access API) =====
  async function ensurePermission(dirHandle, mode = 'readwrite') {
    if (!dirHandle || typeof dirHandle.queryPermission !== 'function') return true;
    try {
      const opts = { mode };
      const q = await dirHandle.queryPermission(opts);
      if (q === 'granted') return true;
      const r = await dirHandle.requestPermission(opts);
      return r === 'granted';
    } catch {
      return false;
    }
  }

  async function* walkDir(dirHandle) {
    for await (const [name, entry] of dirHandle.entries()) {
      if (entry.kind === 'file') {
        if (/\.pdf$/i.test(name)) yield entry; // only PDFs
      } else if (entry.kind === 'directory') {
        yield* walkDir(entry);
      }
    }
  }

  async function listAllPdfFileHandles(dirHandle) {
    const out = [];
    for await (const fh of walkDir(dirHandle)) out.push(fh);
    return out;
  }

  function splitNameExt(name) {
    const m = /^(.*?)(\.[^.]+)?$/.exec(name || '');
    return { base: (m && m[1]) || 'Dokument', ext: (m && m[2]) || '.pdf' };
  }

  async function ensureUniqueName(dirHandle, desired) {
    const { base, ext } = splitNameExt(desired.endsWith('.pdf') ? desired : desired + '.pdf');
    let n = 0;
    while (true) {
      const candidate = n === 0 ? `${base}${ext}` : `${base} (${n})${ext}`;
      try {
        await dirHandle.getFileHandle(candidate, { create: false });
        // exists → try next
        n += 1;
      } catch {
        return n === 0 ? `${base}${ext}` : `${base} (${n})${ext}`;
      }
    }
  }

  async function writeFileTo(dirHandle, name, srcFile) {
    const fileName = await ensureUniqueName(dirHandle, name);
    const fHandle = await dirHandle.getFileHandle(fileName, { create: true });
    const writable = await fHandle.createWritable();
    await writable.write(srcFile);
    await writable.close();
    return fileName;
  }

  // === Browser-Batch Wizard: fehlende Ziel-/Fehler-Ordner abfragen und Browser-Modus aktivieren ===
  async function ensureBrowserHandlesWizard() {
    if (!supportsDirPicker()) return false;
    try {
      let sh = batchSourceHandle;
      if (!sh && (batchSourceDir || '').startsWith('[Browser]')) {
        // Quelle nachfordern
        alert('Bitte QUELLORDNER wählen');
        sh = await window.showDirectoryPicker();
        setBatchSourceHandle(sh);
        setBatchSourceDir(`[Browser] ${sh.name}`);
      }
      // Zielordner
      let th = batchTargetHandle;
      if (!th) {
        alert('Bitte ZIELORDNER wählen (fertige Dateien)');
        th = await window.showDirectoryPicker();
        setBatchTargetHandle(th);
        setBatchTargetDir(`[Browser] ${th.name}`);
      }
      // Fehlerordner
      let eh = batchErrorHandle;
      if (!eh) {
        alert('Bitte FEHLERORDNER wählen (bei Problemen)');
        eh = await window.showDirectoryPicker();
        setBatchErrorHandle(eh);
        setBatchErrorDir(`[Browser] ${eh.name}`);
      }
      return !!(sh && th && eh);
    } catch (e) {
      // abgebrochen → false
      return false;
    }
  }

  async function runBrowserBatch() {
    try {
      setBatchError('');
      setBatchAbort(false);
      console.log('[BATCH] runBrowserBatch START');
      console.log('[BATCH] sourceHandle:', batchSourceHandle);
      console.log('[BATCH] targetHandle:', batchTargetHandle);
      console.log('[BATCH] errorHandle:', batchErrorHandle);
      // permissions
      const okSrc = await ensurePermission(batchSourceHandle, 'read');
      const okTgt = await ensurePermission(batchTargetHandle, 'readwrite');
      const okErr = await ensurePermission(batchErrorHandle, 'readwrite');
      console.log('[BATCH] permissions:', { okSrc, okTgt, okErr });
      if (!okSrc || !okTgt || !okErr) {
        setBatchError('Zugriff verweigert. Bitte Dateisystem-Zugriff für die gewählten Ordner erlauben.');
        return;
      }

      const fileHandles = await listAllPdfFileHandles(batchSourceHandle);
      console.log('[BATCH] PDFs gefunden:', fileHandles.length);
      if (fileHandles.length === 0) {
        setBatchError('Keine PDF-Dateien im Quellordner gefunden.');
        return;
      }
      setBatchTotal(fileHandles.length);
      setBatchDone(0);
      setBatchOk(0);
      setBatchErr(0);
      setBatchLog([]);
      setBatchRunning(true);

      for (let i = 0; i < fileHandles.length; i++) {
        if (batchAbort) break;
        const fh = fileHandles[i];
        let srcFile;
        try {
          srcFile = await fh.getFile();
        } catch (e) {
          setBatchErr((v) => v + 1);
          setBatchDone((v) => v + 1);
          setBatchLog((logs) => [...logs, `Fehler: Kann Datei nicht lesen (${fh.name})`].slice(-200));
          continue;
        }
        setBatchCurrent(srcFile.name);

        // call backend for rename proposal (no text/ocr to keep it light)
        const fd = new FormData();
        fd.append('file', srcFile, srcFile.name);
        fd.append('include_text', '0');
        fd.append('include_ocr', '0');
        fd.append('schema_override', 'auto');

        let destName = '';
        try {
          const res = await fetch(PROCESS_URL, { method: 'POST', body: fd });
          if (!res.ok) throw new Error(`API ${res.status}`);
          const data = await res.json();
          console.log('[BATCH] API response:', data?.proposed, data);
          // match the single-file normalization
          const proposed = data?.proposed || data?.proposed_filename || deriveDefaultName(srcFile.name);
          destName = (proposed || '').trim() || deriveDefaultName(srcFile.name);
          console.log('[BATCH] destName:', destName);
          // write to target
          await writeFileTo(batchTargetHandle, destName, srcFile);
          setBatchOk((v) => v + 1);
          setBatchLog((logs) => [...logs, `OK: ${srcFile.name} → ${destName}`].slice(-200));
        } catch (e) {
          console.error('[BATCH] Error processing file:', e);
          // on error, copy original into error folder
          const fallback = `ERROR - ${srcFile.name}`;
          try { await writeFileTo(batchErrorHandle, fallback, srcFile); } catch {}
          setBatchErr((v) => v + 1);
          setBatchLog((logs) => [...logs, `Fehler: ${srcFile.name} → ${destName || '(keine)'} (${e?.message || e})`].slice(-200));
        } finally {
          setBatchDone((v) => v + 1);
        }
      }
      console.log('[BATCH] Loop finished');
      setBatchCurrent('');
    } finally {
      console.log('[BATCH] runBrowserBatch END');
      setBatchRunning(false);
    }
  }

  const sourcePickerRef = useRef(null);
  const targetPickerRef = useRef(null);
  const errorPickerRef = useRef(null);
  // ===== Batch – Helpers =====
  function openBatchModal() {
    setBatchError("");
    setShowBatchModal(true);
  }

  function closeBatchModal() {
    // stop polling if any
    if (batchPollRef.current) {
      clearInterval(batchPollRef.current);
      batchPollRef.current = null;
    }
    setShowBatchModal(false);
  }

  function supportsDirPicker() {
    return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
  }

  async function pickDirectory(kind) {
    try {
      if (supportsDirPicker()) {
        const handle = await window.showDirectoryPicker();
        const name = handle?.name || "Ordner";
        if (kind === "source") {
          setBatchSourceHandle(handle);
          setBatchSourceDir(`[Browser] ${name}`);
        } else if (kind === "target") {
          setBatchTargetHandle(handle);
          setBatchTargetDir(`[Browser] ${name}`);
        } else if (kind === "error") {
          setBatchErrorHandle(handle);
          setBatchErrorDir(`[Browser] ${name}`);
        }
      } else {
        // Fallback für Browser ohne File System Access API
        const ref = kind === "source" ? sourcePickerRef : kind === "target" ? targetPickerRef : errorPickerRef;
        if (ref && ref.current) ref.current.click();
      }
    } catch (e) {
      // Benutzer hat evtl. abgebrochen – kein Fehler anzeigen
    }
  }

  function onDirInputChange(kind, e) {
    try {
      const files = Array.from(e?.target?.files || []);
      if (files.length === 0) return;
      const first = files[0];
      const rel = first.webkitRelativePath || ""; // z.B. "MeinOrdner/unter/a.pdf"
      const top = rel.split("/")[0] || first.name || "Ordner";
      const label = `[Browser] ${top}`;
      if (kind === "source") {
        setBatchSourceDir(label);
      } else if (kind === "target") {
        setBatchTargetDir(label);
      } else if (kind === "error") {
        setBatchErrorDir(label);
      }
      // Reset, damit derselbe Ordner erneut gewählt werden kann
      if (e && e.target) e.target.value = "";
    } catch {
      // ignoriere
    }
  }

  async function startBatch() {
    try {
      setBatchError("");
      // Auto‑Wizard: Wenn der Quellordner über den Browser‑Picker kommt, fehlende Ordner abfragen
      if (supportsDirPicker() && (((batchSourceDir || "").startsWith("[Browser]")) || batchSourceHandle)) {
        const okWizard = await ensureBrowserHandlesWizard();
        if (okWizard) {
          await runBrowserBatch();
          return;
        }
        // Falls Wizard abgebrochen wurde, nicht fortfahren
        return;
      }
      // Browser Mode: use File System Access API to read/copy locally without server paths
      if (browserModeActive) {
        await runBrowserBatch();
        return;
      }
      if (!batchSourceDir || !batchSourceDir.trim()) {
        setBatchError("Bitte Quellordner (PDFs) angeben.");
        return;
      }
      // Hinweis: Browser-Ordner liefern keinen absoluten Systempfad – der Server kann damit nicht scannen.
      if (!browserModeActive && (batchSourceDir || "").startsWith("[Browser]")) {
        setBatchError("Der gewählte Ordner kommt aus dem Browser-Picker. Bitte den echten Systempfad im Feld eintragen (z. B. /Users/…/Eingang). Oder nutze den Browser‑Modus, indem du Quelle, Ziel **und** Fehlerordner über den Ordner‑Picker auswählst.");
        return;
      }
      // Start-Request an Backend
      const res = await fetch(BATCH_START_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_dir: batchSourceDir.trim(),
          include_text: false,
          include_ocr: false,
          preferred_lang: null,
          schema_override: null
        }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `Start fehlgeschlagen (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const id = data?.batch_id || data?.id || null;
      if (!id) throw new Error("Konnte batch_id aus der Antwort nicht lesen.");

      setBatchId(id);
      setBatchRunning(true);
      setBatchTotal(0);
      setBatchDone(0);
      setBatchOk(0);
      setBatchErr(0);
      setBatchCurrent("");
      setBatchLog([]);

      // Sofort erste Abfrage und danach Polling
      await pollProgress(id, true);
      if (batchPollRef.current) clearInterval(batchPollRef.current);
      batchPollRef.current = setInterval(() => {
        pollProgress(id, false).catch(() => {});
      }, 1500);
    } catch (e) {
      setBatchError(e?.message || String(e) || "Unbekannter Fehler beim Start");
    }
  }

  async function pollProgress(id, first = false) {
    try {
      const url = `${BATCH_PROGRESS_URL}?batch_id=${encodeURIComponent(id)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Progress-Fehler (${res.status})`);
      const data = await res.json();
      const total = Number(data?.total || 0);
      const done = Number(data?.done || 0);
      const ok = Number(data?.ok || 0);
      const err = Number(data?.err || 0);
      const running = Boolean(data?.running);
      const current = String(data?.current || "");
      const logTail = Array.isArray(data?.log) ? data.log : (Array.isArray(data?.log_tail) ? data.log_tail : []);

      setBatchTotal(total);
      setBatchDone(done);
      setBatchOk(ok);
      setBatchErr(err);
      setBatchRunning(running);
      setBatchCurrent(current);
      setBatchLog(logTail);

      if (!running && batchPollRef.current) {
        clearInterval(batchPollRef.current);
        batchPollRef.current = null;
      }
    } catch (e) {
      if (first) setBatchError(e?.message || String(e));
    }
  }

  async function stopBatch() {
    try {
      if (batchRunning && browserModeActive) {
        setBatchAbort(true);
        // UI will stop after current file
        return;
      }
      if (!batchId) return;
      const url = `${BATCH_STOP_URL}?batch_id=${encodeURIComponent(batchId)}`;
      const res = await fetch(url, { method: "POST" });
      if (!res.ok) throw new Error(`Stop fehlgeschlagen (${res.status})`);
      await pollProgress(batchId, false);
    } catch (e) {
      setBatchError(e?.message || String(e));
    }
  }

  // ===== Dropbox Batch helpers (gleiches Verhalten wie HomePage) =====
  const dbxBuildQuery = () => {
    const p = new URLSearchParams();
    p.set("limit", "1");
    if (dbxInbox) p.set("inbox", dbxInbox);
    if (dbxOutbox) p.set("outbox", dbxOutbox);
    if (dbxProcessed) p.set("processed", dbxProcessed);
    if (dbxErrorFolder) p.set("error", dbxErrorFolder);
    return `?${p.toString()}`;
  };

  const fetchDbxBatchStats = async () => {
    try {
      const resp = await fetch(`${DROPBOX_STATS_URL}${dbxBuildQuery()}`);
      if (!resp.ok) return;
      const j = await resp.json();
      const inbox = j.inbox_count ?? j.inbox ?? null;
      const outbox = j.outbox_count ?? j.outbox ?? null;
      const processed = j.processed_count ?? j.processed ?? null;
      const error = j.error_count ?? j.errors ?? j.error ?? null;
      setDbxFolderStats({ inbox, outbox, processed, error });
    } catch (_) {}
  };

  const fetchDbxBatchStatsOnce = async () => {
    try {
      const resp = await fetch(`${DROPBOX_STATS_URL}${dbxBuildQuery()}`);
      if (!resp.ok) return null;
      const j = await resp.json();
      const inbox = j.inbox_count ?? j.inbox ?? null;
      const outbox = j.outbox_count ?? j.outbox ?? null;
      const processed = j.processed_count ?? j.processed ?? null;
      const error = j.error_count ?? j.errors ?? j.error ?? null;
      const obj = { inbox, outbox, processed, error };
      setDbxFolderStats(obj);
      return obj;
    } catch (_) { return null; }
  };

  const dbxPushSpinner = (label = "Verarbeite …") => {
    const item = { id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`, text: label, status: "spinner" };
    setDbxLiveFeed((prev) => [...prev, item].slice(-300));
    return item.id;
  };

  const dbxFinalizeFeed = (id, text, status = "ok") => {
    setDbxLiveFeed((prev) => prev.map((it) => (it.id === id ? { ...it, text, status } : it)));
  };

  const startDbxBatchNow = async () => {
    setDbxBatchError("");
    setDbxBatchResult(null);
    setDbxLiveFeed([]);
    setDbxFolderStats({ inbox: null, outbox: null, processed: null, error: null });
    setDbxProgressPct(5);
    setDbxBatchDone(false);
    setRunningDbxBatch(true);
    let poller = null;
    try {
      const sessionId = localStorage.getItem("sessionId") || Cookies.get("sessionId") || "";
      poller = setInterval(() => { fetchDbxBatchStats(); }, 2000);
      let noWorkRounds = 0;
      let rounds = 0;
      let stop = false;

      while (!stop && rounds < 50) { // safety cap
        rounds += 1;
        const spinnerId = dbxPushSpinner("Verarbeite …");
        const url = `${DROPBOX_RUN_ONCE_URL}${dbxBuildQuery()}`;
        let data = null;
        try {
          const resp = await fetch(url, {
            method: "POST",
            headers: { "Accept": "application/json", "session-id": sessionId },
          });
          if (resp.ok) data = await resp.json();
        } catch (_) {}
        if (!data) throw new Error("Endpoint /rename-me-ocr/api/dropbox/run-once nicht erreichbar.");

        setDbxBatchResult(data);
        const processed = Number(data.processed ?? 0);
        const errors = Number(data.errors ?? 0);
        const trace = Array.isArray(data.trace) ? data.trace : [];

        const okLine = trace.find((t) => /^✔️ OUTBOX: /.test(String(t)));
        const errLine = trace.find((t) => /^❌ ERROR: /.test(String(t)));
        if (okLine) {
          const m = okLine.match(/^✔️ OUTBOX: (.+?) \(orig: (.+?)\)/);
          const text = m ? `${m[1]}  ✓ OUTBOX` : okLine;
          dbxFinalizeFeed(spinnerId, text, "ok");
        } else if (errLine) {
          const m = errLine.match(/^❌ ERROR: (.+?) \(orig: (.+?)\)\s+—\s+Gründe: (.+)$/);
          const text = m ? `${m[1]}  ✗ ERROR – ${m[3]}` : errLine;
          dbxFinalizeFeed(spinnerId, text, "error");
        } else {
          const chk = trace.find((t) => /^\[check\]\s+/.test(String(t)));
          dbxFinalizeFeed(spinnerId, chk || (processed > 0 ? "✓ verarbeitet" : errors > 0 ? "✗ Fehler" : "–"), processed > 0 ? "ok" : (errors > 0 ? "error" : "ok"));
        }

        const statsNow = await fetchDbxBatchStatsOnce();
        const inboxNow = statsNow ? (statsNow.inbox ?? null) : null;
        if (processed === 0) noWorkRounds += 1; else noWorkRounds = 0;
        if (noWorkRounds >= 2 && inboxNow === 0) stop = true;
        setDbxProgressPct((p) => Math.min(98, processed > 0 ? p + 6 : p + 1));
      }

      await fetchDbxBatchStats();
      setDbxProgressPct(100);
      setDbxBatchDone(true);
    } catch (e) {
      setDbxBatchError(e?.message || String(e));
    } finally {
      if (poller) clearInterval(poller);
      setRunningDbxBatch(false);
    }
  };

  const handleDbxBatchRename = () => {
    setDbxBatchError("");
    setDbxBatchResult(null);
    setDbxLiveFeed([]);
    setDbxFolderStats({ inbox: null, outbox: null, processed: null, error: null });
    setDbxProgressPct(0);
    setDbxBatchDone(false);
    setDbxEditMode(false); // Felder zunächst ausgegraut
    setShowDbxModal(true);
  };

  const closeDbxBatchModal = () => {
    if (runningDbxBatch) return;
    setShowDbxModal(false);
  };

  // Prüft, ob die ausgewählte Datei wirklich lokal lesbar ist (Cloud-Platzhalter vermeiden)
  async function ensureReadable(selectedFile) {
    if (!selectedFile) throw new Error("Keine Datei ausgewählt.");
    try {
      // Nur einen kleinen Teil lesen, um die Verfügbarkeit zu prüfen
      const probe = selectedFile.slice(0, 1024);
      await probe.arrayBuffer();
      return true;
    } catch (e) {
      const msg = String(e && e.message || e || "");
      // Häufige Browser/FS-Fehler, wenn Cloud-Only/OneDrive offline ist
      if (msg.toLowerCase().includes("notreadable") || msg.toLowerCase().includes("not found") || msg.toLowerCase().includes("permission") || msg.toLowerCase().includes("security")) {
        throw new Error("Die Datei ist nicht lokal lesbar. Liegt sie evtl. nur als Cloud‑Platzhalter (z. B. OneDrive/Dropbox/iCloud) vor? Bitte 'Immer auf diesem Gerät behalten' wählen und erneut versuchen.");
      }
      throw new Error("Konnte die Datei nicht lesen (evtl. Cloud‑Platzhalter). Bitte lokal verfügbar machen und erneut versuchen.");
    }
  }
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // === V2-Pipeline Toggle (Cookie-persistiert) ===
  const [useV2, setUseV2] = useState(() => Cookies.get("imho_use_v2") === "1");
  const PROCESS_URL = useV2 ? PROCESS_URL_V2 : PROCESS_URL_V1;
  function toggleV2(val) {
    setUseV2(val);
    Cookies.set("imho_use_v2", val ? "1" : "0", { expires: 365 });
  }

  const [payload, setPayload] = useState(null);
  const [schema, setSchema] = useState("auto"); // Nutzerwahl (Standard: automatisch)
  const [autoSchema, setAutoSchema] = useState(null); // Erkannt vom Backend

  const [filename, setFilename] = useState("");
  const [showText, setShowText] = useState(false);
  const [fullText, setFullText] = useState("");
  const [showTrace, setShowTrace] = useState(false);
  const [showOCR, setShowOCR] = useState(false);
  const [ocrRaw, setOcrRaw] = useState(null);
  const [evidenceRaw, setEvidenceRaw] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // === BBox-Bearbeitung State ===
  const [modifiedBboxes, setModifiedBboxes] = useState({}); // { field_kind: { offset_x, offset_y, offset_w, offset_h } }
  const [editingBbox, setEditingBbox] = useState(null); // Aktuell bearbeitete Box (kind)
  const [bboxEditMode, setBboxEditMode] = useState(false); // Bearbeitungsmodus aktiv?
  const [savingCorrections, setSavingCorrections] = useState(false);

  // OCR viewer UI state
  const [ocrPage, setOcrPage] = useState(0);
  const [ocrZoom, setOcrZoom] = useState(1);
  const [ocrShowGrid, setOcrShowGrid] = useState(true);
  const [ocrShowBoxes, setOcrShowBoxes] = useState(false);
  const [ocrConfMin, setOcrConfMin] = useState(0);
  const [ocrLang, setOcrLang] = useState('deu'); // default OCR language: German

  function triggerFilePicker() {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }


  function selectFile(f) {
    if (!f) return;
    setFile(f);
    setError("");
    setPayload(null);
    setFilename("");
    setAutoSchema(null);
    setSchema("auto");
    setFullText("");
    setShowText(false);
    setShowTrace(false);
    setShowOCR(false);
    setOcrRaw(null);
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  async function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // PDF aus DataTransferItems bevorzugen
    let f = null;
    const items = e.dataTransfer?.items;
    if (items && items.length) {
      for (const it of items) {
        if (it.kind === "file") {
          const cand = it.getAsFile();
          if (cand && /\.pdf$/i.test(cand.name)) { f = cand; break; }
        }
      }
    }
    // Fallback: normale Files-Liste
    if (!f) {
      const files = Array.from(e.dataTransfer?.files || []);
      f = files.find(x => /\.pdf$/i.test(x.name)) || files[0] || null;
    }
    if (!f) { setError("Bitte eine PDF-Datei ablegen."); return; }

    try {
      await ensureReadable(f);
    } catch (preflightErr) {
      setError(preflightErr.message || "Datei nicht lesbar (evtl. Cloud‑Platzhalter).");
      return;
    }
    selectFile(f);
  }

  function onFileChange(e) {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    selectFile(f);
  }

  async function processWithOverride(schemaOverride, options = {}) {
    const { includeOcr = false, overridePrefix = null, overrideDate = null, overrideSender = null, overrideInvoice = null } = options;
    if (loading) return;
    if (!file) {
      setLoading(false);
      setError("Bitte zuerst eine PDF auswählen.");
      return;
    }
    setLoading(true);
    setError("");
    setPayload(null);
    try {
      await ensureReadable(file);
    } catch (preflightErr) {
      setError(preflightErr.message || "Datei nicht lesbar (evtl. Cloud‑Platzhalter)");
      setLoading(false);
      return;
    }

    try {
      // Clientseitige Größenprüfung, um 413 vom Server vorzubeugen
      if (file && file.size && file.size > MAX_UPLOAD_MB * 1024 * 1024) {
        throw new Error(`Die Datei ist größer als ${MAX_UPLOAD_MB} MB. Bitte kleinere Datei wählen oder IMHO_MAX_UPLOAD_MB erhöhen.`);
      }
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("include_text", "1");
      if (includeOcr) {
        fd.append("include_ocr", "1");
        if (ocrLang) fd.append("preferred_lang", String(ocrLang));
      }
      if (schemaOverride && schemaOverride !== "") fd.append("schema_override", String(schemaOverride));
      if (overridePrefix) fd.append("override_prefix", String(overridePrefix));
      if (overrideDate) fd.append("override_date", String(overrideDate));
      if (overrideSender) fd.append("override_sender", String(overrideSender));
      if (overrideInvoice) fd.append("override_invoice", String(overrideInvoice));

      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      let res;
      try {
        res = await fetch(PROCESS_URL, {
          method: "POST",
          body: fd,
          signal: controller.signal,
          redirect: "manual",
        });
        if (res && (res.status === 307 || res.status === 308)) {
          const alt = PROCESS_URL.endsWith("/") ? PROCESS_URL.slice(0, -1) : PROCESS_URL + "/";
          res = await fetch(alt, { method: "POST", body: fd, signal: controller.signal, redirect: "manual" });
        }
      } catch (err) {
        clearTimeout(t);
        const msg = String((err && err.message) || err || "").toLowerCase();
        if (err && (err.name === "AbortError" || msg.includes("abort"))) {
          throw new Error(`Zeitüberschreitung nach ${Math.round(REQUEST_TIMEOUT_MS / 1000)}s – API nicht erreichbar? (${PROCESS_URL})`);
        }
        if (msg.includes("request-body-stream") || msg.includes("body stream") || msg.includes("stream exhausted")) {
          throw new Error("Der Browser konnte den Datei-Stream nicht senden (Safari-Problem). Bitte erneut versuchen oder Chrome/Firefox nutzen. Cache leeren kann helfen.");
        }
        throw err;
      } finally {
        clearTimeout(t);
      }

      if (!res.ok) {
        let message = `Fehler beim Verarbeiten (${res.status}).`;
        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("application/json")) {
          const err = await res.json().catch(() => null);
          if (err) {
            if (Array.isArray(err.detail)) {
              message = err.detail.map((d) => d.msg || d.detail || String(d)).join("; ");
            } else if (err.detail) {
              message = typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
            }
          }
        } else {
          const ttxt = await res.text().catch(() => "");
          if (ttxt) message = ttxt;
        }
        if (/no file provided/i.test(message)) {
          const altFd = new FormData();
          altFd.append("upload", file, file.name);
          altFd.append("include_text", "1");
          if (schemaOverride && schemaOverride !== "") {
            altFd.append("schema_override", String(schemaOverride));
          }
          const res2 = await fetch(PROCESS_URL, { method: "POST", body: altFd, redirect: "manual" });
          if (res2.ok) {
            res = res2;
          } else {
            throw new Error(message);
          }
        } else {
          throw new Error(message);
        }
      }

      const data = await res.json();

      // OCR raw extraction
      const ocr = data?.raw?.ocr || null;
      setOcrRaw(ocr);
      // Evidence extraction and normalization
      const evRaw = data?.raw?.evidence || data?.evidence || null;
      setEvidenceRaw(normalizeEvidence(evRaw));
      if (includeOcr && ocr) {
        setShowOCR(true);
      }

      // Normalisieren: altes Frontend-Shape befüllen
      const normalized = {
        ...data,
        // kompatible Aliasse
        doc_type: data?.schema || data?.doc_type,
        proposed_filename: data?.proposed || data?.proposed_filename,
        date: data?.fields?.date || data?.date || null,
        sender_norm: data?.fields?.sender || data?.sender_norm || "",
        iban_hits: Array.isArray(data?.fields?.ibans) ? data.fields.ibans : (data?.iban_hits || []),
        matched_iban: data?.fields?.matched_iban || data?.matched_iban || null,
        db_matched_ibans: Array.isArray(data?.fields?.db_ibans) ? data.fields.db_ibans : (Array.isArray(data?.db_matched_ibans) ? data.db_matched_ibans : []),
        account_suffix: data?.fields?.account || data?.account_suffix || "",
        invoice_number_raw: data?.fields?.invoice_no || data?.invoice_number_raw || "",
        statement: data?.fields?.statement || data?.statement || "",
        scores: data?.fields?.scores || data?.scores || {},
        engine: data?.engine || data?.ocr_engine || "",
        // Trace kann Strings oder Objekte enthalten
        trace: Array.isArray(data?.trace) ? data.trace : [],
        prefix: data?.prefix || data?.fields?.prefix || "",
        prefix_candidates: Array.isArray(data?.prefix_candidates) ? data.prefix_candidates : [],
        date_candidates: Array.isArray(data?.date_candidates) ? data.date_candidates : [],
        invoice_number_6: data?.invoice_number_6 || null,
        sender_candidates: Array.isArray(data?.sender_candidates) ? data.sender_candidates : [],
        invoice_candidates: Array.isArray(data?.invoice_candidates) ? data.invoice_candidates : [],
        // Hybrid-Info: Welche Engine hat welches Feld gefunden
        hybrid_info: data?.fields?._hybrid_info || null,
        validation: data?.fields?._validation || null,
        // Einzelne Source-Felder für einfachen Zugriff
        prefix_source: data?.fields?.prefix_source || data?.fields?._hybrid_info?.prefix_source || null,
        date_source: data?.fields?.date_source || data?.fields?._hybrid_info?.date_source || null,
        sender_source: data?.fields?.sender_source || data?.fields?._hybrid_info?.sender_source || null,
        invoice_source: data?.fields?.invoice_no_source || data?.fields?._hybrid_info?.invoice_no_source || null,
        // NEU: Schema-Metadaten für dynamische Anzeige
        schema_meta: data?.schema_meta || null,
        // BWA-spezifische Felder
        mandant: data?.fields?.mandant || data?.fields?.MANDANT || data?.fields?.mandanten_nr || null,
        period: data?.fields?.period_str || (data?.fields?.period_year && data?.fields?.period_month ? `${data.fields.period_year}-${data.fields.period_month}` : null),
        bwa_variant: data?.fields?.bwa_variant || data?.fields?.BWA_VARIANT || null,
        // Versorger-spezifische Felder
        zaehler_nr: data?.fields?.zaehler_nr || data?.fields?.zaehlernummer || data?.fields?.vertragsnummer || null,
        // Darlehensnummer (optional)
        darlehensnummer: data?.fields?.darlehensnummer || null,
        darlehensnummer_candidates: Array.isArray(data?.fields?.darlehensnummer_candidates) ? data.fields.darlehensnummer_candidates : [],
        // Leistungszeitraum (optional)
        leistungszeitraum: data?.fields?.leistungszeitraum || null,
        leistungszeitraum_candidates: Array.isArray(data?.fields?.leistungszeitraum_candidates) ? data.fields.leistungszeitraum_candidates : [],
      };
      setPayload(normalized);

      const auto = normalized?.doc_type || null;
      setAutoSchema(auto);
      if (schemaOverride && schemaOverride !== "auto") {
        setSchema(schemaOverride);
      } else {
        setSchema("auto");
      }

      const proposed = normalized?.proposed_filename || deriveDefaultName(file?.name);
      setFilename(proposed);

      // Volltext: vom Backend (full_text) oder aus Seiten zusammenbauen
      if (typeof data?.full_text === "string" && data.full_text.length > 0) {
        setFullText(data.full_text);
      } else if (Array.isArray(data?.raw?.pages) && data.raw.pages.length > 0) {
        setFullText(data.raw.pages.join("\n\n"));
      } else {
        setFullText("");
      }
    } catch (e) {
      setError(e.message || "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  }

  async function handlePreview() {
    return processWithOverride("auto", { includeOcr: true });
  }

  async function handleSchemaChange(next) {
    setSchema(next);
    if (!file) return; // ohne Datei nichts tun
    await processWithOverride(next, { includeOcr: false });
  }

  async function handleOCR() {
    return processWithOverride(schema, { includeOcr: true });
  }

  // Top-level handlers for prefix/date/invoice/sender candidate application (for JSX usage)
  async function applyPrefixCandidate(px) {
    if (!file) return;
    // nutze das aktuell wirksame Schema (auto → vom Backend erkannt)
    await processWithOverride(effectiveSchema(), { includeOcr: false, overridePrefix: px });
  }

  async function applyDateCandidate(d) {
    if (!file) return;
    await processWithOverride(effectiveSchema(), { includeOcr: false, overrideDate: d });
  }

  async function applyInvoiceCandidate(inv) {
    if (!file) return;
    await processWithOverride(effectiveSchema(), { includeOcr: false, overrideInvoice: inv });
  }

  async function applySenderCandidate(s) {
    if (!file) return;
    await processWithOverride(effectiveSchema(), { includeOcr: false, overrideSender: s });
  }

  // === BBox-Bearbeitungsfunktionen ===
  
  // Berechnet den Offset einer Box basierend auf der Differenz zwischen Original und aktueller Position
  function calculateBboxOffset(originalRect, newRect) {
    return {
      offset_x: Math.round(newRect.left - originalRect.left),
      offset_y: Math.round(newRect.top - originalRect.top),
      offset_w: Math.round(newRect.width - originalRect.width),
      offset_h: Math.round(newRect.height - originalRect.height),
    };
  }

  // Handler für Drag-Ende einer Evidence-Box
  function handleBboxDragEnd(kind, originalRect, newLeft, newTop) {
    const newRect = { ...originalRect, left: newLeft, top: newTop };
    const offset = calculateBboxOffset(originalRect, newRect);
    setModifiedBboxes(prev => ({
      ...prev,
      [kind]: { ...prev[kind], ...offset }
    }));
  }

  // Handler für Resize-Ende einer Evidence-Box
  function handleBboxResizeEnd(kind, originalRect, newWidth, newHeight) {
    const currentOffset = modifiedBboxes[kind] || { offset_x: 0, offset_y: 0, offset_w: 0, offset_h: 0 };
    setModifiedBboxes(prev => ({
      ...prev,
      [kind]: {
        ...currentOffset,
        offset_w: Math.round(newWidth - originalRect.width),
        offset_h: Math.round(newHeight - originalRect.height),
      }
    }));
  }

  // Alle Modifikationen zurücksetzen
  function resetBboxModifications() {
    setModifiedBboxes({});
    setEditingBbox(null);
  }

  // Speichert alle BBox-Korrekturen ans Backend
  async function saveBboxCorrections() {
    if (Object.keys(modifiedBboxes).length === 0) {
      alert("Keine Änderungen zum Speichern.");
      return;
    }

    const senderNorm = payload?.sender_norm || payload?.fields?.sender || "";
    if (!senderNorm || senderNorm === "###") {
      alert("Kein Absender erkannt. Korrekturen können nur pro Absender gespeichert werden.");
      return;
    }

    setSavingCorrections(true);
    const errors = [];
    const saved = [];

    for (const [fieldKind, offsets] of Object.entries(modifiedBboxes)) {
      try {
        const fd = new FormData();
        fd.append("sender_norm", senderNorm);
        fd.append("field_kind", fieldKind);
        fd.append("offset_x", String(offsets.offset_x || 0));
        fd.append("offset_y", String(offsets.offset_y || 0));
        fd.append("offset_w", String(offsets.offset_w || 0));
        fd.append("offset_h", String(offsets.offset_h || 0));
        
        // Original-Wert für Debugging mitgeben
        const originalValue = payload?.fields?.[fieldKind] || payload?.[fieldKind] || "";
        if (originalValue) {
          fd.append("original_value", String(originalValue));
        }

        const res = await fetch(EVIDENCE_SAVE_CORRECTION_URL, {
          method: "POST",
          body: fd,
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || `HTTP ${res.status}`);
        }

        saved.push(fieldKind);
      } catch (err) {
        errors.push(`${fieldKind}: ${err.message}`);
      }
    }

    setSavingCorrections(false);

    if (errors.length > 0) {
      alert(`Fehler beim Speichern:\n${errors.join("\n")}\n\nErfolgreich: ${saved.join(", ") || "keine"}`);
    } else {
      alert(`Korrekturen gespeichert für: ${saved.join(", ")}\n\nDiese werden bei zukünftigen Dokumenten von "${senderNorm}" automatisch angewendet.`);
      setModifiedBboxes({});
      setBboxEditMode(false);
    }
  }

  // Prüft ob es ungespeicherte Änderungen gibt
  const hasModifiedBboxes = Object.keys(modifiedBboxes).length > 0;

  async function handleCopy(text) {
    const t = String(text || "");
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(t);
        alert("In Zwischenablage kopiert.");
      } else {
        const ta = document.createElement("textarea");
        ta.value = t;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        alert("In Zwischenablage kopiert.");
      }
    } catch (e) {
      alert("Kopieren fehlgeschlagen: " + (e?.message || e));
    }
  }
  function downloadJson(obj, filename = "ocr-result.json") {
    try {
      const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download fehlgeschlagen: " + (e?.message || e));
    }
  }


  function deriveDefaultName(origName) {
    if (!origName) return "Dokument.pdf";
    const base = origName.replace(/\.[^.]+$/, "");
    return `${base}.pdf`;
  }

  function prettyLabel(value) {
    return SCHEMA_OPTIONS.find((o) => o.value === value)?.label || value || "–";
  }

  function effectiveSchema() {
    return schema === "auto" ? autoSchema || "unbekannt" : schema;
  }

  // Helper: Extracts valid IBANs from mixed text
  function extractIbans(list) {
    if (!Array.isArray(list)) return [];
    const re = /\b[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}\b/g; // generic IBAN pattern (15–34 char total)
    const out = new Set();
    for (const s of list) {
      const m = String(s || "").toUpperCase().match(re);
      if (m) for (const hit of m) out.add(hit);
    }
    return Array.from(out);
  }

  // Evidence bounding box normalization helper
  function normalizeEvidence(ev) {
    if (!ev) return null;

    const normalizeItem = (it) => {
      const out = { ...it };
      // Array formats → {x,y,w,h}
      const arr = Array.isArray(it?.bbox_px) ? it.bbox_px
                : Array.isArray(it?.bboxPx) ? it.bboxPx
                : Array.isArray(it?.bbox_pixels) ? it.bbox_pixels
                : null;
      if (arr && arr.length === 4) {
        const x1 = Number(arr[0]) || 0;
        const y1 = Number(arr[1]) || 0;
        const x2 = Number(arr[2]) || 0;
        const y2 = Number(arr[3]) || 0;
        out.bbox = { x: x1, y: y1, w: Math.max(1, x2 - x1), h: Math.max(1, y2 - y1) };
        return out;
      }

      // Object formats
      if (it?.bbox && typeof it.bbox === 'object') {
        if (typeof it.bbox.x1 === 'number' && typeof it.bbox.y1 === 'number' &&
            typeof it.bbox.x2 === 'number' && typeof it.bbox.y2 === 'number') {
          const { x1, y1, x2, y2 } = it.bbox;
          out.bbox = { x: x1, y: y1, w: Math.max(1, x2 - x1), h: Math.max(1, y2 - y1) };
          return out;
        }
        if (typeof it.bbox.x === 'number' && typeof it.bbox.w === 'number') {
          out.bbox = { x: it.bbox.x, y: it.bbox.y || 0, w: it.bbox.w, h: it.bbox.h || 1 };
          return out;
        }
      }

      // If no bbox, leave as is (bboxToCssRect will ignore)
      return out;
    };

    const copy = { ...ev };

    // Normalize pages[].items
    if (Array.isArray(ev.pages)) {
      copy.pages = ev.pages.map((p) => {
        const items = Array.isArray(p?.items) ? p.items.map(normalizeItem) : [];
        return { ...p, items };
      });
    }

    // Normalize top-level items too
    if (Array.isArray(ev.items)) {
      copy.items = ev.items.map(normalizeItem);
    }

    return copy;
  }

  function handleDownload() {
    if (!file) return;
    const name = (filename || file.name || "Dokument.pdf").trim();
    const safeName = name.endsWith(".pdf") ? name : `${name}.pdf`;

    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // === Duplikate für HIG-Eingangsrechnungen erstellen ===
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicatesResult, setDuplicatesResult] = useState(null);

  async function handleCreateDuplicates() {
    setDuplicatesLoading(true);
    setDuplicatesResult(null);
    try {
      const res = await fetch(DUPLICATES_CREATE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      setDuplicatesResult(data);
      if (data.success) {
        alert(`✅ Duplikate erstellt!\n\nErstellt: ${data.created}\nÜbersprungen: ${data.skipped}\nFehler: ${data.errors}`);
      } else {
        alert(`❌ Fehler beim Erstellen der Duplikate.\n\n${data.trace?.slice(-3).join('\n') || 'Unbekannter Fehler'}`);
      }
    } catch (err) {
      console.error("Duplikate-Fehler:", err);
      alert(`❌ Fehler: ${err.message}`);
    } finally {
      setDuplicatesLoading(false);
    }
  }

  // === BWA UStVA Extraktor ===
  const [bwaLoading, setBwaLoading] = useState(false);
  const [bwaResult, setBwaResult] = useState(null);

  async function handleExtractBwaUstva() {
    setBwaLoading(true);
    setBwaResult(null);
    try {
      const res = await fetch(BWA_EXTRACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      setBwaResult(data);
      if (data.success) {
        let message = `✅ BWA UStVA-Extraktion abgeschlossen!\n\nErstellt: ${data.created}\nÜbersprungen: ${data.skipped}\nFehler: ${data.errors}`;
        
        if (data.no_ustva_found && data.no_ustva_found.length > 0) {
          message += `\n\n⚠️ ${data.no_ustva_found.length} Datei(en) ohne UStVA-Seite:\n`;
          message += data.no_ustva_found.slice(0, 10).map(f => `• ${f}`).join('\n');
          if (data.no_ustva_found.length > 10) {
            message += `\n... und ${data.no_ustva_found.length - 10} weitere`;
          }
        }
        
        alert(message);
      } else {
        alert(`❌ Fehler bei der BWA UStVA-Extraktion.\n\n${data.trace?.slice(-3).join('\n') || 'Unbekannter Fehler'}`);
      }
    } catch (err) {
      console.error("BWA UStVA-Fehler:", err);
      alert(`❌ Fehler: ${err.message}`);
    } finally {
      setBwaLoading(false);
    }
  }

  // === N26 Kontoauszug Splitter ===
  const [n26Loading, setN26Loading] = useState(false);
  const [n26Result, setN26Result] = useState(null);

  async function handleN26Split() {
    setN26Loading(true);
    setN26Result(null);
    try {
      const res = await fetch(N26_SPLIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      setN26Result(data);
      if (data.success) {
        let message = `✅ N26-Splitting abgeschlossen!\n\nVerarbeitet: ${data.processed}\nErstellt: ${data.created}\nÜbersprungen: ${data.skipped}\nFehler: ${data.errors}`;
        
        // Details zu gesplitteten Dateien
        const splitFiles = (data.files || []).filter(f => f.status === 'split');
        if (splitFiles.length > 0) {
          message += `\n\n📄 Gesplittet:`;
          for (const f of splitFiles.slice(0, 5)) {
            message += `\n• ${f.name} → ${f.ibans} Konten`;
          }
          if (splitFiles.length > 5) {
            message += `\n... und ${splitFiles.length - 5} weitere`;
          }
        }
        
        alert(message);
      } else {
        alert(`❌ Fehler beim N26-Splitting.\n\n${data.trace?.slice(-3).join('\n') || 'Unbekannter Fehler'}`);
      }
    } catch (err) {
      console.error("N26-Split-Fehler:", err);
      alert(`❌ Fehler: ${err.message}`);
    } finally {
      setN26Loading(false);
    }
  }

  // === OCR Overlay Viewer (PDF.js + relative bbox overlay) ===
  function OcrOverlayViewer({
    file,
    ocrRaw,
    evidenceRaw,
    allowedEvidenceKinds = null, // NEU: nur diese Evidence-Typen anzeigen (null = alle)
    page = 0,
    onPageChange = () => {},
    zoom = 1,
    onZoomChange = () => {},
    showGrid = true,
    onToggleGrid = () => {},
    showBoxes = true,
    onToggleBoxes = () => {},
    confMin = 0,
    onConfMinChange = () => {},
    onClose = () => {},
  }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const pageRef = useRef(null);
    // current page meta (from OCR or Evidence) for aspect ratio & mm grid
    // Fallback to evidenceRaw.pages_meta if ocrRaw.pages_meta is not available
    const meta = Array.isArray(ocrRaw?.pages_meta) 
      ? ocrRaw.pages_meta[page || 0] 
      : (Array.isArray(evidenceRaw?.pages_meta) ? evidenceRaw.pages_meta[page || 0] : null);
    const [pdfErr, setPdfErr] = useState("");
    const [pdfPages, setPdfPages] = useState(0);
    const [rendering, setRendering] = useState(false);
    // --- durable UI prefs ---
    const measureCacheRef = useRef(new Map());
    const prefGet = (k, def) => {
      try {
        const v = localStorage.getItem(k);
        if (v === null || v === undefined) return def;
        return v === '1' || v === 'true';
      } catch { return def; }
    };
    const [showTextOverlay, setShowTextOverlay] = useState(prefGet('ocr_showTextOverlay', true));
    const [textScale, setTextScale] = useState(1);
    // dauerhaft aktiv: gleiche Schriftgröße je Zeile (kann in UI umgeschaltet werden, aber Standard bleibt an)
    const [uniformText, setUniformText] = useState(prefGet('ocr_uniformText', true));
    const [forceSolidBlack, setForceSolidBlack] = useState(prefGet('ocr_forceSolidBlack', false));
    const [globalUniform, setGlobalUniform] = useState(prefGet('ocr_globalUniform', false));
    const [preventOverlap, setPreventOverlap] = useState(prefGet('ocr_preventOverlap', true));

    // Evidence Toggle (Prefix/Date/Invoice/Sender/Keywords)
    const [showEvidence, setShowEvidence] = useState(prefGet('ocr_showEvidence', true));
    useEffect(() => { localStorage.setItem('ocr_showEvidence', showEvidence ? '1' : '0'); }, [showEvidence]);

    // Collapsible Settings Panel
    const [showSettings, setShowSettings] = useState(false);

    const KIND_COLORS = {
      keyword: 'rgba(33,150,243,0.40)',   // blue
      schema_keyword: 'rgba(33,150,243,0.40)', // blue, like keyword
      prefix:  'rgba(76,175,80,0.35)',    // green
      date:    'rgba(255,152,0,0.35)',    // orange
      invoice: 'rgba(233,30,99,0.35)',    // pink
      sender:  'rgba(156,39,176,0.35)',   // purple
      iban:    'rgba(0,150,136,0.35)',    // teal
      // BWA-spezifische Typen
      mandant: 'rgba(121,85,72,0.40)',    // brown
      period:  'rgba(255,152,0,0.35)',    // orange (wie date)
      variant: 'rgba(103,58,183,0.35)',   // deep purple
      // Versorger-spezifische Typen
      zaehler: 'rgba(233,30,99,0.35)',    // pink (wie invoice)
      // Kontoauszug
      statement: 'rgba(63,81,181,0.35)',  // indigo
      // Sonstige
      person:  'rgba(156,39,176,0.35)',   // purple
      objekt:  'rgba(0,150,136,0.35)',    // teal
      level:   'rgba(244,67,54,0.35)',    // red
      default: 'rgba(0,0,0,0.18)',
    };
    useEffect(() => { localStorage.setItem('ocr_showTextOverlay', showTextOverlay ? '1' : '0'); }, [showTextOverlay]);
    useEffect(() => { localStorage.setItem('ocr_uniformText', uniformText ? '1' : '0'); }, [uniformText]);
    useEffect(() => { localStorage.setItem('ocr_forceSolidBlack', forceSolidBlack ? '1' : '0'); }, [forceSolidBlack]);
    useEffect(() => { localStorage.setItem('ocr_globalUniform', globalUniform ? '1' : '0'); }, [globalUniform]);
    useEffect(() => { localStorage.setItem('ocr_preventOverlap', preventOverlap ? '1' : '0'); }, [preventOverlap]);

    // Helper to reset viewer prefs and toggles
    function resetViewerPrefs() {
      try {
        ['ocr_showTextOverlay','ocr_uniformText','ocr_forceSolidBlack','ocr_globalUniform','ocr_preventOverlap','ocr_showEvidence'].forEach(k => {
          try { localStorage.removeItem(k); } catch (_) {}
        });
      } catch (_) {}
      setShowTextOverlay(false);
      setUniformText(true);
      setForceSolidBlack(true);
      setGlobalUniform(false);
      setPreventOverlap(true);
      setShowEvidence(true);
      // Ensure boxes/evidence are visible via parent toggles if needed
      if (typeof showBoxes === 'boolean' && !showBoxes && typeof onToggleBoxes === 'function') onToggleBoxes();
      if (typeof showGrid === 'boolean' && !showGrid && typeof onToggleGrid === 'function') onToggleGrid();
    }
    // Hard default: Wort-Text Overlay AN, damit Wörter immer sichtbar sind.
    useEffect(() => {
        try { localStorage.setItem('ocr_showTextOverlay', '1'); } catch (_) {}
      }, []);
    const measCtxRef = useRef(null);
    const overlayFontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, sans-serif';
    // const measureCacheRef = useRef(new Map()); // already above
    const WIDTH_FUDGE = 2;       // extra pixels to avoid right/bottom clipping
    const MAX_SHRINK = 0.35;     // do not shrink per‑word font more than 35%

    useEffect(() => {
      if (!measCtxRef.current) {
        const c = document.createElement('canvas');
        measCtxRef.current = c.getContext('2d');
      }
    }, []);

    function textWidthPx(txt, px){
      const ctx = measCtxRef.current;
      const key = `${px}|${String(txt || '')}`;
      const cache = measureCacheRef.current;
      if (cache.has(key)) return cache.get(key);
      if (!ctx) {
        const rough = String(txt || '').length * (px * 0.55);
        cache.set(key, rough);
        return rough;
      }
      ctx.font = `${px}px ${overlayFontFamily}`;
      const w = ctx.measureText(String(txt || '')).width;
      cache.set(key, w);
      return w;
    }

    // Load PDF.js and document from the chosen file
    useEffect(() => {
      let aborted = false;
      async function load() {
        setPdfErr("");
        setPdfPages(0);
        try {
          if (!file) {
            setPdfErr("Keine PDF-Datei ausgewählt.");
            return;
          }
          let pdfjsLib;
          let usingLegacy = false;
          
          // Robuster PDF.js Import mit mehreren Fallbacks
          try {
            // Versuche zuerst den Legacy-Build (besser für ältere Browser)
            pdfjsLib = await import(/* webpackChunkName: "pdfjs-legacy" */ 'pdfjs-dist/legacy/build/pdf.mjs');
            usingLegacy = true;
          } catch (legacyErr) {
            console.warn('pdfjs legacy import failed, trying standard:', legacyErr);
            try {
              // Fallback auf Standard-Build
              pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf.mjs');
            } catch (stdErr) {
              console.warn('pdfjs standard import failed, trying bare import:', stdErr);
              // Letzter Fallback
              pdfjsLib = await import('pdfjs-dist');
            }
          }

          try {
            const ver = pdfjsLib?.version || '4.10.38';
            const workerUrl = usingLegacy
              ? `https://unpkg.com/pdfjs-dist@${ver}/legacy/build/pdf.worker.min.mjs`
              : `https://unpkg.com/pdfjs-dist@${ver}/build/pdf.worker.min.mjs`;
            pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
          } catch (_) {}

          const buf = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
          if (aborted) return;
          setPdfPages(pdf.numPages);

          const renderPage = async () => {
            if (!canvasRef.current || !wrapRef.current) return;
            setRendering(true);
            try {
              const pno = Math.min(Math.max(1, (page || 0) + 1), pdf.numPages);
              const pg = await pdf.getPage(pno);

              // Base viewport (page units)
              const baseViewport = pg.getViewport({ scale: 1 });

              // Container CSS width (height is irrelevant for width-fit)
              const hostRect = (pageRef.current || wrapRef.current).getBoundingClientRect();
              const containerCssW = Math.max(1, hostRect.width || baseViewport.width);

              // Fit-to-width (keep aspect ratio). Zoom multiplies this base scale.
              const scaleW = containerCssW / baseViewport.width;
              const cssScale = Math.max(0.2, zoom) * scaleW;

              // Create CSS viewport (what we *see*)
              const cssViewport = pg.getViewport({ scale: cssScale });

              // Device pixel ratio for crisp rendering
              const dpr = window.devicePixelRatio || 1;

              // Canvas sizing: internal buffer vs. CSS display size
              const c = canvasRef.current;
              const ctx = c.getContext('2d');
              // CSS size
              c.style.width = `${Math.floor(cssViewport.width)}px`;
              c.style.height = `${Math.floor(cssViewport.height)}px`;
              // Internal pixel buffer
              c.width = Math.floor(cssViewport.width * dpr);
              c.height = Math.floor(cssViewport.height * dpr);
              // Reset and scale context for DPR
              ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
              ctx.clearRect(0, 0, cssViewport.width, cssViewport.height);

              await pg.render({ canvasContext: ctx, viewport: cssViewport }).promise;
            } finally {
              setRendering(false);
            }
          };

          await renderPage();
        } catch (e) {
          setPdfErr(e?.message || String(e));
        }
      }
      load();
      return () => { aborted = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, page, zoom]);

    // Helper: compute canvas size (for overlay) and mm grid spacing
    function useCanvasSize() {
      const [sz, setSz] = useState({ w: 0, h: 0, mmPx: 0 });
      useEffect(() => {
        const el = pageRef.current;
        const rect = el ? el.getBoundingClientRect() : null;
        const w = rect ? Math.round(rect.width) : 0;
        const h = rect ? Math.round(rect.height) : 0;
        let mmPx = 0;
        if (meta && w > 0 && meta.width_px > 0 && meta.px_per_mm > 0) {
          const scaleCss = w / meta.width_px; // how much the original PDF width is scaled to CSS px
          mmPx = meta.px_per_mm * scaleCss;
        }
        setSz({ w, h, mmPx });
      }, [ocrRaw, evidenceRaw, page, zoom, rendering, meta]);
      return sz;
    }
    const { w: canvasW, h: canvasH, mmPx } = useCanvasSize();
    const words = Array.isArray(ocrRaw?.details) ? (ocrRaw.details[page || 0] || []) : [];

    // --- Evidence helpers ---
    // DEBUG: Log evidence rendering context
    useEffect(() => {
      if (evidenceRaw) {
        console.log('[EVIDENCE-DEBUG] evidenceRaw:', evidenceRaw);
        console.log('[EVIDENCE-DEBUG] meta:', meta);
        console.log('[EVIDENCE-DEBUG] canvasW:', canvasW, 'canvasH:', canvasH);
        console.log('[EVIDENCE-DEBUG] showEvidence:', showEvidence);
        const items = Array.isArray(evidenceRaw.pages)
          ? (evidenceRaw.pages[page || 0]?.items || [])
          : (evidenceRaw.items || []);
        console.log('[EVIDENCE-DEBUG] items to render:', items);
      }
    }, [evidenceRaw, meta, canvasW, canvasH, showEvidence, page]);

    function bboxToCssRect(b) {
      // Supports relative, mm and px bboxes
      // Preferred: x_rel,y_rel,w_rel,h_rel in [0..1] relative to page
      if (b && typeof b.x_rel === 'number') {
        const left = Math.round((b.x_rel || 0) * canvasW);
        const top = Math.round((b.y_rel || 0) * canvasH);
        const width = Math.max(1, Math.ceil((b.w_rel || 0) * canvasW));
        const height = Math.max(1, Math.ceil((b.h_rel || 0) * canvasH));
        console.log('[bboxToCssRect] using x_rel:', { left, top, width, height });
        return { left, top, width, height };
      }
      // mm-based with px_per_mm from meta
      if (b && typeof b.x_mm === 'number' && meta && meta.px_per_mm) {
        const scaleCss = canvasW > 0 && meta.width_px > 0 ? (canvasW / meta.width_px) : 1;
        const mm2px = (mm) => Math.round(mm * meta.px_per_mm * scaleCss);
        const left = mm2px(b.x_mm || 0);
        const top = mm2px(b.y_mm || 0);
        const width = Math.max(1, mm2px(b.w_mm || 0));
        const height = Math.max(1, mm2px(b.h_mm || 0));
        console.log('[bboxToCssRect] using x_mm:', { left, top, width, height });
        return { left, top, width, height };
      }
      // absolute px at original page resolution
      if (b && typeof b.x === 'number' && meta && meta.width_px > 0 && meta.height_px > 0) {
        const sx = canvasW / meta.width_px;
        const sy = canvasH / meta.height_px;
        const left = Math.round((b.x || 0) * sx);
        const top = Math.round((b.y || 0) * sy);
        const width = Math.max(1, Math.ceil((b.w || 0) * sx));
        const height = Math.max(1, Math.ceil((b.h || 0) * sy));
        console.log('[bboxToCssRect] using absolute px:', { left, top, width, height });
        return { left, top, width, height };
      }
      console.log('[bboxToCssRect] returning null! b:', b, 'meta:', meta);
      return null;
    }

    // Group words into horizontal bands (lines) so we can apply a uniform font size per line
    const bandByIndex = new Map();
    const bandHeights = [];
    const bandTopPx = [];
    const bandMembers = []; // indices of words per band
    if (canvasW > 0 && canvasH > 0 && Array.isArray(words)) {
      const visibles = [];
      words.forEach((w, idx) => {
        const conf = typeof w?.conf === 'number' ? w.conf : (typeof w?.confidence === 'number' ? w.confidence : 100);
        if (conf < confMin) return;
        const yr = Number(w?.y_rel || 0);
        const hr = Number(w?.h_rel || 0);
        const ymid = (yr + hr / 2) * canvasH;
        const hpx = Math.max(1, Math.ceil(hr * canvasH));
        const topPx = Math.max(0, Math.round(yr * canvasH));
        visibles.push({ idx, ymid, hpx, topPx });
      });
      visibles.sort((a, b) => a.ymid - b.ymid);
      const threshold = Math.max(4, canvasH * 0.005); // ~0.5% of page height
      let current = [];
      let lastY = null;
      const bands = [];
      for (const it of visibles) {
        if (lastY === null || Math.abs(it.ymid - lastY) <= threshold) {
          current.push(it);
          lastY = lastY === null ? it.ymid : (lastY * 0.7 + it.ymid * 0.3);
        } else {
          bands.push(current);
          current = [it];
          lastY = it.ymid;
        }
      }
      if (current.length) bands.push(current);
      bands.forEach((arr, bi) => {
        const heights = arr.map(x => x.hpx).sort((a, b) => a - b);
        let chosen = 12;
        if (heights.length) {
          const n = heights.length;
          const lo = heights[Math.max(0, Math.floor(n * 0.2) - 1)];
          const hi = heights[Math.min(n - 1, Math.ceil(n * 0.8))];
          const med = heights[Math.floor(n / 2)];
          chosen = Math.max(8, Math.min(36, Math.min(hi, Math.max(lo || med, med))));
        }
        bandHeights[bi] = chosen;
        const topMin = Math.min(...arr.map(x => x.topPx));
        bandTopPx[bi] = Number.isFinite(topMin) ? Math.max(0, Math.floor(topMin)) : 0;
        const indices = arr.map(x => x.idx);
        bandMembers[bi] = indices;
        indices.forEach(ix => bandByIndex.set(ix, bi));
      });
    }

    // Precompute a per-band font size that fits all words horizontally and vertically
    const bandFontPx = [];
    if (bandMembers.length && canvasW > 0 && canvasH > 0) {
      for (let bi = 0; bi < bandMembers.length; bi++) {
        const base = Math.max(6, Math.floor((bandHeights[bi] || 12) * 0.90 * textScale));
        let minScale = 1;
        for (const idx of bandMembers[bi] || []) {
          const w = words[idx];
          if (!w) continue;
          const wr = Number(w?.w_rel || 0);
          const width = Math.max(1, Math.ceil(wr * canvasW) + WIDTH_FUDGE);
          const text = String(w?.text || '');
          const measured = textWidthPx(text, base) || 1;
          const ratio = Math.min(1, (width - 1) / Math.max(8, measured));
          minScale = Math.min(minScale, ratio);
        }
        const targetH = Math.max(6, Math.floor((bandHeights[bi] || 12)));
        let fp = Math.floor(Math.min(base, targetH - 1) * minScale);
        fp = Math.max(6, fp);
        bandFontPx[bi] = fp;
      }
    }

    // Determine a typical body text size (median across fitted band sizes)
    let globalFontPx = null;
    if (bandFontPx.length) {
      const hs = bandFontPx.filter(h => h >= 8 && h <= 48).slice().sort((a, b) => a - b);
      if (hs.length) globalFontPx = hs[Math.floor(hs.length / 2)];
    } else if (bandHeights.length) {
      const hs = bandHeights.filter(h => h >= 8 && h <= 36).slice().sort((a, b) => a - b);
      if (hs.length) globalFontPx = hs[Math.floor(hs.length / 2)];
    }

    return (
      <div style={{ borderTop: "1px solid #eee", paddingTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0 }}>OCR-Overlay</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => {
              // Quick mode: only 4‑eckige Kästen sichtbar, kein Text-Doppel
              if (!showBoxes && typeof onToggleBoxes === 'function') onToggleBoxes();
              if (!showEvidence) setShowEvidence(true);
              if (showTextOverlay) setShowTextOverlay(false);
            }}>
              Nur Kästen
            </button>
            <button onClick={resetViewerPrefs}>Reset</button>
            <button onClick={onClose}>Schließen</button>
          </div>
        </div>

        {/* Settings Toggle Button */}
        <button
          className={`ocr-settings-toggle ${showSettings ? 'is-open' : ''}`}
          onClick={() => setShowSettings(v => !v)}
          type="button"
        >
          <span className="toggle-icon">▼</span>
          {showSettings ? 'Einstellungen ausblenden' : 'Einstellungen anzeigen'}
        </button>

        {/* Collapsible Settings Panel */}
        <div className={`ocr-settings-panel ${showSettings ? 'is-open' : ''}`}>
          {/* Navigation & Zoom */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <strong>Seite:</strong>
            <select
              value={page}
              onChange={(e)=> onPageChange(parseInt(e.target.value,10))}
              disabled={!ocrRaw || !Array.isArray(ocrRaw?.pages_meta)}
            >
              {Array.isArray(ocrRaw?.pages_meta) ? ocrRaw.pages_meta.map((_, i) => (
                <option key={i} value={i}>Seite {i+1}</option>
              )) : <option value={0}>1</option>}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <strong>Zoom:</strong>
            <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={(e)=> onZoomChange(parseFloat(e.target.value))} />
            <span style={{ minWidth: 48, textAlign: 'right' }}>{(zoom*100).toFixed(0)}%</span>
          </div>

          {/* Overlay Options */}
          <label>
            <input type="checkbox" checked={showGrid} onChange={onToggleGrid} /> mm‑Raster
          </label>
          <label>
            <input type="checkbox" checked={showBoxes} onChange={onToggleBoxes} /> Wort‑Boxen
          </label>
          <label>
            <input type="checkbox" checked={showTextOverlay} onChange={() => setShowTextOverlay((v) => !v)} /> Wort‑Text
          </label>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <strong>Textgröße:</strong>
            <input type="range" min="0.5" max="2" step="0.1" value={textScale} onChange={(e)=> setTextScale(parseFloat(e.target.value || "1"))} />
            <span style={{ minWidth: 48, textAlign: 'right' }}>{Math.round(textScale * 100)}%</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <strong>Conf ≥</strong>
            <input type="number" value={confMin} min={0} max={100} step={1} onChange={(e)=> onConfMinChange(Math.max(0, Math.min(100, parseInt(e.target.value||"0",10))))} style={{ width: 70 }} />
          </div>

          <label>
            <input type="checkbox" checked={uniformText} onChange={() => setUniformText(v => !v)} /> Zeilen‑Einheitsgröße
          </label>
          <label>
            <input type="checkbox" checked={forceSolidBlack} onChange={() => setForceSolidBlack(v => !v)} /> 100% Schwarz
          </label>
          <label>
            <input type="checkbox" checked={globalUniform} onChange={() => setGlobalUniform(v => !v)} /> Seiten‑Einheitsgröße
          </label>
          <label>
            <input type="checkbox" checked={preventOverlap} onChange={() => setPreventOverlap(v => !v)} /> Überlappung verhindern
          </label>
          <label>
            <input type="checkbox" checked={showEvidence} onChange={() => setShowEvidence(v => !v)} /> Evidence
          </label>
          
          {/* BBox-Bearbeitung */}
          {showEvidence && (
            <>
              <label style={{ color: bboxEditMode ? '#ff9800' : 'inherit' }}>
                <input 
                  type="checkbox" 
                  checked={bboxEditMode} 
                  onChange={() => {
                    setBboxEditMode(v => !v);
                    if (bboxEditMode) setEditingBbox(null);
                  }} 
                /> ✏️ Boxen bearbeiten
              </label>
              {hasModifiedBboxes && (
                <button
                  onClick={saveBboxCorrections}
                  disabled={savingCorrections}
                  style={{
                    background: '#4caf50',
                    color: 'white',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: savingCorrections ? 'wait' : 'pointer',
                    fontSize: 12,
                  }}
                >
                  {savingCorrections ? '⏳ Speichern...' : `💾 ${Object.keys(modifiedBboxes).length} Korrektur(en) speichern`}
                </button>
              )}
              {hasModifiedBboxes && (
                <button
                  onClick={resetBboxModifications}
                  style={{
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  ↩️ Zurücksetzen
                </button>
              )}
            </>
          )}

          {/* Meta Info */}
          <div className="ocr-settings-meta">
            <span><strong>Engine:</strong> {ocrRaw?.engine || "–"}</span>
            <span><strong>Sprache:</strong> {ocrRaw?.lang || "–"}</span>
            <span><strong>Seiten:</strong> {Array.isArray(ocrRaw?.pages_meta) ? ocrRaw.pages_meta.length : (Array.isArray(ocrRaw?.pages) ? ocrRaw.pages.length : 0)}</span>
          </div>
          {pdfErr && <div className="ocr-meta-error" style={{ gridColumn: '1 / -1' }}>PDF-Render: {pdfErr}</div>}
        </div>

        {/* Viewer */}
        <div
          ref={wrapRef}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            border: '1px solid #eee',
            borderRadius: 6,
            // bei Zoom > 100% scrollbar statt abgeschnitten
            overflow: 'auto',
            background: '#fff'
          }}
        >
          <div
            ref={pageRef}
            style={{
              position: 'relative',
              width: '100%',
              // keep the page's true aspect ratio so height is never squashed
              aspectRatio: (meta && meta.width_px && meta.height_px)
                ? `${meta.width_px} / ${meta.height_px}`
                : '1 / 1.4142', // A-series fallback
              background: '#fff'
            }}
          >
            {/* mm grid tied to the page box, not the outer wrapper */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                display: showGrid && mmPx > 2 ? 'block' : 'none',
                backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.08) 1px, transparent 1px)',
                backgroundSize: `${mmPx}px ${mmPx}px`,
                zIndex: 2
              }}
            />

            {/* PDF background */}
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                display: showTextOverlay ? 'none' : 'block',
                zIndex: 0
              }}
            />

            {/* Evidence overlay (keyword/prefix/date/invoice/sender/iban etc.) */}
            <div
              style={{
                position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                pointerEvents: bboxEditMode ? 'auto' : 'none', zIndex: 5, // above boxes/text
                display: showEvidence ? 'block' : 'none'
              }}
            >
              {showEvidence && canvasW > 0 && canvasH > 0 && evidenceRaw && (
                Array.isArray(evidenceRaw.pages)
                  ? (evidenceRaw.pages[page || 0]?.items || [])
                  : (evidenceRaw.items || [])
              )
                // NEU: Filtern nach erlaubten Evidence-Kinds (wenn angegeben)
                .filter((it) => {
                  if (!allowedEvidenceKinds || allowedEvidenceKinds.length === 0) return true;
                  const kind = (it?.kind || it?.type || 'default').toLowerCase();
                  // Schema-Keywords und Keywords immer anzeigen
                  if (kind === 'keyword' || kind === 'schema_keyword') return true;
                  return allowedEvidenceKinds.includes(kind);
                })
                .map((it, i) => {
                const kind = (it?.kind || it?.type || 'default').toLowerCase();
                const color = KIND_COLORS[kind] || KIND_COLORS.default;
                const rect = bboxToCssRect(it?.bbox || it);
                if (!rect) return null;
                
                // Wende gespeicherte Modifikationen an
                const mod = modifiedBboxes[kind] || {};
                const modLeft = rect.left + (mod.offset_x || 0);
                const modTop = rect.top + (mod.offset_y || 0);
                const modWidth = rect.width + (mod.offset_w || 0);
                const modHeight = rect.height + (mod.offset_h || 0);
                
                const isEditing = bboxEditMode && editingBbox === kind;
                const isModified = mod.offset_x || mod.offset_y || mod.offset_w || mod.offset_h;
                
                return (
                  <DraggableResizableBox
                    key={`ev-${i}`}
                    kind={kind}
                    color={color}
                    left={modLeft}
                    top={modTop}
                    width={modWidth}
                    height={modHeight}
                    originalRect={rect}
                    isEditMode={bboxEditMode}
                    isEditing={isEditing}
                    isModified={isModified}
                    onSelect={() => setEditingBbox(kind)}
                    onDragEnd={(newLeft, newTop) => handleBboxDragEnd(kind, rect, newLeft, newTop)}
                    onResizeEnd={(newWidth, newHeight) => handleBboxResizeEnd(kind, rect, newWidth, newHeight)}
                  />
                );
              })}
            </div>

            {/* Overlay words */}
            <div
              style={{
                position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 3
              }}
            >
              {showBoxes && canvasW > 0 && canvasH > 0 && Array.isArray(words) && words.map((w, idx) => {
                const conf = typeof w?.conf === 'number' ? w.conf : (typeof w?.confidence === 'number' ? w.confidence : 100);
                if (conf < confMin) return null;
                const xr = Number(w?.x_rel || 0), yr = Number(w?.y_rel || 0);
                const wr = Number(w?.w_rel || 0), hr = Number(w?.h_rel || 0);
                const left = Math.round(xr * canvasW);
                const top = Math.round(yr * canvasH);
                // Use ceil+WIDTH_FUDGE to avoid right/bottom cutoff
                const width = Math.max(1, Math.ceil(wr * canvasW) + WIDTH_FUDGE);
                const height = Math.max(1, Math.ceil(hr * canvasH) + WIDTH_FUDGE);
                return (
                  <div key={idx}
                    title={`${w?.text || ''} (${conf}%)`}
                    style={{
                      position: 'absolute',
                      left, top, width, height,
                      border: 'none',
                      background: 'transparent',
                      boxSizing: 'border-box',
                    }}
                  />
                );
              })}
            </div>

            {/* Overlay text (actual OCR words) */}
            <div
              style={{
                position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 4
              }}
            >
              {showTextOverlay && canvasW > 0 && canvasH > 0 && Array.isArray(words) && words.map((w, idx) => {
                const conf = typeof w?.conf === 'number' ? w.conf : (typeof w?.confidence === 'number' ? w.confidence : 100);
                if (conf < confMin) return null;
                const xr = Number(w?.x_rel || 0), yr = Number(w?.y_rel || 0);
                const wr = Number(w?.w_rel || 0), hr = Number(w?.h_rel || 0);
                const left = Math.round(xr * canvasW);
                const top = Math.round(yr * canvasH);
                // Use ceil+WIDTH_FUDGE to avoid right/bottom cutoff
                const width = Math.max(1, Math.ceil(wr * canvasW) + WIDTH_FUDGE);
                const height = Math.max(1, Math.ceil(hr * canvasH) + WIDTH_FUDGE);
                const text = String(w?.text || '');

                // font-size calculation and shrink logic (refactored)
                const bandIdx = bandByIndex.get(idx);
                const bandH = (bandIdx !== undefined && uniformText) ? (bandHeights[bandIdx] || height) : height;
                const bandTop = (bandIdx !== undefined && uniformText) ? (bandTopPx[bandIdx] ?? top) : top;

                // unified font-size selection with final safety clamps (width & height)
                let usedFontPx;
                if (globalUniform && globalFontPx) {
                  usedFontPx = Math.max(6, Math.floor(globalFontPx));
                } else if (uniformText && bandIdx !== undefined) {
                  // per-line unified size, pre-fitted across the line
                  usedFontPx = Math.max(6, Math.floor((bandFontPx[bandIdx] || (bandHeights[bandIdx] * 0.90 * textScale))));
                } else {
                  // per-word fallback: fit height first
                  usedFontPx = Math.max(6, Math.floor(height * 0.92 * textScale));
                }

                // Always respect the available line height (avoid top/bottom clipping)
                const maxByHeight = Math.max(6, Math.floor((bandH || height) * 0.95));
                if (usedFontPx > maxByHeight) usedFontPx = maxByHeight;

                // Final safety: if the measured width still exceeds the box, shrink proportionally.
                let wpx2 = textWidthPx(text, usedFontPx);
                if (wpx2 > width - 1) {
                  const ratio = (width - 1) / Math.max(8, wpx2);
                  const shrink = Math.max(1 - MAX_SHRINK, Math.min(1, ratio));
                  usedFontPx = Math.max(6, Math.floor(usedFontPx * shrink));
                  wpx2 = textWidthPx(text, usedFontPx);
                }
                const topAdj = Math.floor(bandTop + Math.max(0, (bandH - usedFontPx) / 2));
                const opacity = forceSolidBlack ? 1 : Math.max(0.45, Math.min(1, conf / 90));
                const nearOverflow = (wpx2 > width * 0.98);
                return (
                  <div
                    key={`t-${idx}`}
                    className="ocr-word-text"
                    style={{
                      position: 'absolute',
                      left: Math.floor(left),
                      top: topAdj,
                      width,
                      height: bandH,
                      fontFamily: overlayFontFamily,
                      fontSize: usedFontPx,
                      lineHeight: `${Math.ceil(usedFontPx * 1.08)}px`,
                      color: '#000',
                      opacity,
                      pointerEvents: 'none',
                      overflow: preventOverlap ? 'hidden' : 'visible',
                      textOverflow: 'clip',
                      whiteSpace: 'nowrap',
                      paddingRight: 3,
                      letterSpacing: 0,
                    }}
                    title={`${text} (${conf}%)`}
                  >
                    {text}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick legend */}
        <div style={{ fontSize: 12, opacity: .75, marginTop: 6 }}>
          Boxen basieren auf relativen Koordinaten (x_rel/y_rel/w_rel/h_rel). Zoom &amp; Canvas‑Breite sind beliebig, die Position bleibt deckungsgleich.
        </div>
        {/* Evidence Legend */}
        {showEvidence && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
            {Object.entries(KIND_COLORS).filter(([k]) => k !== 'default').map(([k,v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, background: v, border: `1px solid ${v.replace(/0\.\d+\)$/, '1)')}`, borderRadius: 2 }} />
                <span style={{ textTransform: 'capitalize' }}>{k}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rename-container" style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* Header */}
      <div className="rename-header">
        <h1>Dokument umbenennen</h1>
        <p className="rename-subtitle">PDF hochladen → Dateiname anpassen → speichern</p>
      </div>

      {/* Action Cards - Dropzone und Batch nebeneinander */}
      <div className="action-cards">
        <div
          className={`action-card dropzone-card ${isDragging ? 'dropzone-active' : ''} ${file ? 'dropzone-has-file' : ''}`}
          onClick={triggerFilePicker}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') triggerFilePicker(); }}
          aria-label="PDF hierher ziehen oder klicken"
        >
          <div className="action-card-icon">📄</div>
          {file ? (
            <>
              <div className="action-card-title">Ausgewählt</div>
              <div className="dropzone-filename">{file.name}</div>
            </>
          ) : (
            <>
              <div className="action-card-title">PDF auswählen</div>
              <div className="action-card-hint">Ablegen oder klicken</div>
            </>
          )}
        </div>

        <button
          type="button"
          className="action-card batch-card"
          onClick={handleDbxBatchRename}
          disabled={runningDbxBatch}
        >
          <div className="action-card-icon">📁</div>
          <div className="action-card-title">Batch</div>
          <div className="action-card-hint">Mehrere Dateien</div>
        </button>

        <input
          ref={fileInputRef}
          id="fileInput"
          type="file"
          accept="application/pdf,.pdf"
          onChange={onFileChange}
          style={{ display: "none" }}
        />
      </div>
      {/* Dropbox Batch Modal */}
      {showDbxModal && (
        <div className="modal-overlay">
          <div className="modal-content" role="dialog" aria-modal="true">
            <h3>Dropbox Batch läuft …</h3>
            <p className="modal-subtitle">Batch läuft – hol dir einen Tee ☕️, wir sagen Bescheid, wenn es durch ist.</p>
            <div className="dbx-folder-grid">
              <label>Inbox-Ordner</label>
              <input value={dbxInbox} onChange={(e)=>{ setDbxInbox(e.target.value); localStorage.setItem('dbx_inbox', e.target.value); }} placeholder="/Inbox" disabled={!dbxEditMode || runningDbxBatch} />
              <label>Outbox-Ordner</label>
              <input value={dbxOutbox} onChange={(e)=>{ setDbxOutbox(e.target.value); localStorage.setItem('dbx_outbox', e.target.value); }} placeholder="/Outbox" disabled={!dbxEditMode || runningDbxBatch} />
              <label>Archiv-Ordner</label>
              <input value={dbxProcessed} onChange={(e)=>{ setDbxProcessed(e.target.value); localStorage.setItem('dbx_processed', e.target.value); }} placeholder="/Processed" disabled={!dbxEditMode || runningDbxBatch} />
              <label>Error-Ordner</label>
              <input value={dbxErrorFolder} onChange={(e)=>{ setDbxErrorFolder(e.target.value); localStorage.setItem('dbx_error', e.target.value); }} placeholder="/Error" disabled={!dbxEditMode || runningDbxBatch} />
            </div>

            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${dbxProgressPct}%` }} />
            </div>

            <div className="stats-grid">
              <div className="stat-card"><div className="stat-label">Inbox</div><div className="stat-value">{dbxFolderStats.inbox ?? '–'}</div></div>
              <div className="stat-card"><div className="stat-label">Outbox</div><div className="stat-value">{dbxFolderStats.outbox ?? '–'}</div></div>
              <div className="stat-card"><div className="stat-label">Archiv</div><div className="stat-value">{dbxFolderStats.processed ?? '–'}</div></div>
              <div className="stat-card"><div className="stat-label">Error</div><div className="stat-value">{dbxFolderStats.error ?? '–'}</div></div>
            </div>

            <div className="live-feed">
              <div className="feed-header">Live-Feed</div>
              <ul>
                {dbxLiveFeed.map((it, idx) => (
                  <li key={it.id || idx} className={`feed-item feed-${it.status}`}>
                    <span className="feed-icon">
                      {it.status === 'spinner' ? '○' : it.status === 'ok' ? '✓' : '✗'}
                    </span>
                    {String(it.text)}
                  </li>
                ))}
              </ul>
            </div>

            {dbxBatchDone && (
              <div className="batch-done">
                <div className="done-icon">✓</div>
                <div>Fertig! Alle Dateien wurden verarbeitet.</div>
              </div>
            )}

            {dbxBatchError && (
              <div className="batch-error">Fehler: {dbxBatchError}</div>
            )}

            <div className="modal-actions">
              {!runningDbxBatch ? (
                <button onClick={startDbxBatchNow}>Start</button>
              ) : (
                <button disabled>Läuft…</button>
              )}
              <button
                onClick={() => setDbxEditMode(v => !v)}
                disabled={runningDbxBatch}
              >
                {dbxEditMode ? "Sperren" : "Bearbeiten"}
              </button>
              <button
                onClick={() => {
                  if (runningDbxBatch) return;
                  setDbxInbox(DBX_DEFAULTS.inbox || "");
                  setDbxOutbox(DBX_DEFAULTS.outbox || "");
                  setDbxProcessed(DBX_DEFAULTS.processed || "");
                  setDbxErrorFolder(DBX_DEFAULTS.error || "");
                  try {
                    localStorage.removeItem("dbx_inbox");
                    localStorage.removeItem("dbx_outbox");
                    localStorage.removeItem("dbx_processed");
                    localStorage.removeItem("dbx_error");
                  } catch (_) {}
                }}
                disabled={runningDbxBatch}
              >
                Reset
              </button>
              <button onClick={closeDbxBatchModal} disabled={runningDbxBatch}>
                {runningDbxBatch ? 'Bitte warten…' : 'Schließen'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Dateiname (bearbeitbar) */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <label style={{ minWidth: 160, fontWeight: 600 }}>Dateiname</label>
        <input
          className="filename-input"
          type="text"
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          placeholder="Vorgeschlagener Dateiname"
          style={{ flex: 1 }}
        />
        <button
          onClick={() => {
            if (filename) {
              navigator.clipboard.writeText(filename);
            }
          }}
          disabled={!filename}
          title="Dateiname in Zwischenablage kopieren"
          style={{ whiteSpace: "nowrap", padding: "4px 10px" }}
        >
          📋 Kopieren
        </button>
      </div>

      {/* Aktionen */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button onClick={handlePreview} disabled={loading || !file}>
          {loading ? "Verarbeite…" : "Vorschau"}
        </button>
        <button onClick={handleOCR} disabled={loading || !file}>
          {loading ? "…" : "Dokument anzeigen"}
        </button>
        <button onClick={handleDownload} disabled={!file || !filename}>
          Herunterladen
        </button>
        <button 
          onClick={handleCreateDuplicates} 
          disabled={duplicatesLoading}
          style={{ backgroundColor: '#6c5ce7', color: 'white' }}
          title="Erstellt Duplikate für HIG-Eingangsrechnungen in Dropbox-Outbox"
        >
          {duplicatesLoading ? "Erstelle…" : "Duplikate"}
        </button>
        <button 
          onClick={handleExtractBwaUstva} 
          disabled={bwaLoading}
          style={{ backgroundColor: '#9b59b6', color: 'white' }}
          title="Extrahiert UStVA-Seiten aus BWA-Gesamt-Dateien in Dropbox-Outbox"
        >
          {bwaLoading ? "Extrahiere…" : "BWA"}
        </button>
        <button 
          onClick={handleN26Split} 
          disabled={n26Loading}
          style={{ backgroundColor: '#00d4aa', color: 'white' }}
          title="Splittet N26-Kontoauszüge nach IBAN (Hauptkonto + Spaces) in Dropbox-Inbox"
        >
          {n26Loading ? "Splitte…" : "N26 Split"}
        </button>
      </div>
      <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>API: {PROCESS_URL}</span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: useV2 ? 'bold' : 'normal', color: useV2 ? '#e67e22' : 'inherit' }}>
          <input
            type="checkbox"
            checked={useV2}
            onChange={e => toggleV2(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          V2 (KI-Pipeline)
        </label>
      </div>


      {/* Fehler */}
      {error && <p className="rename-error">Fehler: {error}</p>}

      {/* Vorschau-Bereich */}
      {payload && (
        <div className="preview-section">
          <h2 style={{ marginTop: 0 }}>Vorschau</h2>
          <div className="preview-grid">
            <div className="preview-label">Schema</div>
            <div className="preview-value">{prettyLabel(effectiveSchema())}</div>

            {/* ========== DYNAMISCHE FELDER aus schema_meta ========== */}
            {payload.schema_meta?.display_fields?.length > 0 ? (
              // Nutze schema_meta.display_fields für dynamische Anzeige
              payload.schema_meta.display_fields.map((field, idx) => {
                // Wert ermitteln: entweder direkt aus field.value oder aus payload
                let value = field.value;
                if (!value) {
                  // Fallback auf bekannte Payload-Keys
                  const keyMap = {
                    prefix: payload.prefix,
                    date: payload.date,
                    invoice_no: payload.invoice_number_raw,
                    sender: payload.sender_norm,
                    mandant: payload.mandant,
                    period: payload.period,
                    bwa_variant: payload.bwa_variant,
                    zaehler_nr: payload.zaehler_nr,
                    bank: payload.sender_norm,
                    statement_n: payload.statement,
                  };
                  value = keyMap[field.key];
                }
                
                // Nicht anzeigen wenn kein Wert
                if (!value) return null;
                
                // Source ermitteln
                let source = field.source;
                if (!source) {
                  const srcMap = {
                    prefix: payload.prefix_source,
                    date: payload.date_source,
                    invoice_no: payload.invoice_source,
                    sender: payload.sender_source,
                  };
                  source = srcMap[field.key];
                }
                
                // Candidates ermitteln
                let candidates = field.candidates || [];
                if (candidates.length === 0) {
                  const candMap = {
                    prefix: payload.prefix_candidates,
                    date: payload.date_candidates,
                    invoice_no: payload.invoice_candidates,
                    sender: payload.sender_candidates,
                  };
                  candidates = candMap[field.key] || [];
                }
                
                // Click-Handler für Candidates
                const applyCandidate = (cand) => {
                  if (field.key === 'prefix') applyPrefixCandidate(cand);
                  else if (field.key === 'date' || field.key === 'period') applyDateCandidate(cand);
                  else if (field.key === 'invoice_no') applyInvoiceCandidate(cand);
                  else if (field.key === 'sender') applySenderCandidate(cand);
                };
                
                return (
                  <React.Fragment key={field.key || idx}>
                    <div className="preview-label">{field.label}</div>
                    <div>
                      <span className="value-badge">{value}</span>
                      {source && (
                        <span 
                          className={source?.includes('hybrid') ? 'source-badge-hybrid' : 'source-badge'} 
                          title="Erkannt via"
                        >
                          {source}
                        </span>
                      )}
                      {Array.isArray(candidates) && candidates
                        .filter((c) => c && c !== value)
                        .slice(0, 2)
                        .map((cand, i) => (
                          <button
                            key={i}
                            onClick={() => applyCandidate(cand)}
                            title={`Als ${field.label} übernehmen`}
                            className="chip-btn"
                          >{cand}</button>
                        ))}
                    </div>
                  </React.Fragment>
                );
              })
            ) : (
              // Fallback: Alte statische Anzeige wenn kein schema_meta vorhanden
              <>
                {payload?.prefix && (
                  <>
                    <div className="preview-label">Prefix</div>
                    <div>
                      <span className="value-badge">{payload.prefix}</span>
                      {payload.prefix_source && (
                        <span className={payload.prefix_source?.includes('hybrid') ? 'source-badge-hybrid' : 'source-badge'} title="Erkannt via">
                          {payload.prefix_source}
                        </span>
                      )}
                      {Array.isArray(payload.prefix_candidates) && payload.prefix_candidates
                        .filter((p) => p && p !== payload.prefix)
                        .slice(0,2)
                        .map((p, i) => (
                          <button
                            key={i}
                            onClick={() => applyPrefixCandidate(p)}
                            title="Als Prefix übernehmen"
                            className="chip-btn"
                          >{p}</button>
                        ))}
                    </div>
                  </>
                )}

                {payload?.date && (
                  <>
                    <div className="preview-label">Datum</div>
                    <div>
                      <span className="value-badge">{payload.date}</span>
                      {payload.date_source && (
                        <span className={payload.date_source?.includes('hybrid') ? 'source-badge-hybrid' : 'source-badge'} title="Erkannt via">
                          {payload.date_source}
                        </span>
                      )}
                      {Array.isArray(payload.date_candidates) && payload.date_candidates
                        .filter((d) => d && d !== payload.date)
                        .slice(0,2)
                        .map((d, i) => (
                          <button
                            key={i}
                            onClick={() => applyDateCandidate(d)}
                            title="Als Datum übernehmen"
                            className="chip-btn"
                          >{d}</button>
                        ))}
                    </div>
                  </>
                )}

                {payload?.invoice_number_raw && (
                  <>
                    <div className="preview-label">Rechnungsnummer</div>
                    <div>
                      <span className="value-badge">
                        {payload.invoice_number_raw}
                        {payload.invoice_number_6 ? ` (…${payload.invoice_number_raw?.startsWith('#') ? '#' : ''}${payload.invoice_number_6})` : ""}
                      </span>
                      {payload.invoice_source && (
                        <span className={payload.invoice_source?.includes('hybrid') ? 'source-badge-hybrid' : 'source-badge'} title="Erkannt via">
                          {payload.invoice_source}
                        </span>
                      )}
                      {Array.isArray(payload.invoice_candidates) && payload.invoice_candidates
                        .filter((x) => x && x !== payload.invoice_number_raw)
                        .slice(0,2)
                        .map((alt, i) => (
                          <button key={i} className="chip-btn" onClick={() => applyInvoiceCandidate(alt)}>{alt}</button>
                        ))}
                    </div>
                  </>
                )}

                {payload?.sender_norm && (
                  <>
                    <div className="preview-label">Absender (norm.)</div>
                    <div>
                      <span className="value-badge">{payload.sender_norm || '–'}</span>
                      {payload.sender_source && (
                        <span className={payload.sender_source?.includes('hybrid') ? 'source-badge-hybrid' : 'source-badge'} title="Erkannt via">
                          {payload.sender_source}
                        </span>
                      )}
                      {Array.isArray(payload.sender_candidates) && payload.sender_candidates
                        .filter((s) => s && s !== payload.sender_norm)
                        .slice(0,2)
                        .map((s, i) => (
                          <button key={i} className="chip-btn" onClick={() => applySenderCandidate(s)}>{s}</button>
                        ))}
                    </div>
                  </>
                )}

                {payload?.darlehensnummer && (
                  <>
                    <div className="preview-label">Darlehensnummer</div>
                    <div>
                      <span className="value-badge">{payload.darlehensnummer}</span>
                      {Array.isArray(payload.darlehensnummer_candidates) && payload.darlehensnummer_candidates
                        .filter((d) => d && d !== payload.darlehensnummer)
                        .slice(0,2)
                        .map((d, i) => (
                          <button key={i} className="chip-btn" title="Alternative Darlehensnummer">{d}</button>
                        ))}
                    </div>
                  </>
                )}

                {payload?.leistungszeitraum && (
                  <>
                    <div className="preview-label">Leistungszeitraum</div>
                    <div>
                      <span className="value-badge">{payload.leistungszeitraum}</span>
                      {Array.isArray(payload.leistungszeitraum_candidates) && payload.leistungszeitraum_candidates
                        .filter((l) => l && l !== payload.leistungszeitraum)
                        .slice(0,2)
                        .map((l, i) => (
                          <button key={i} className="chip-btn" title="Alternativer Zeitraum">{l}</button>
                        ))}
                    </div>
                  </>
                )}
              </>
            )}
            {/* ========== ENDE DYNAMISCHE FELDER ========== */}

            {typeof payload?.confidence === "number" && (
              <>
                <div className="preview-label">Confidence</div>
                <div className="preview-value">{(payload.confidence * 100).toFixed(1)}%</div>
              </>
            )}

            <div className="preview-label">Engine</div>
            <div className="preview-value">
              {payload.engine || "–"}
              {payload.hybrid_info?.used_fallback && (
                <span className="source-badge-hybrid" style={{ marginLeft: 8 }} title="Hybrid: Mindestens ein Feld kam von der anderen Engine">
                  + Hybrid-Fallback
                </span>
              )}
            </div>

            {payload.doc_subtype && (
              <>
                <div className="preview-label">Untertyp</div>
                <div className="preview-value">{payload.doc_subtype}</div>
              </>
            )}

            {payload.statement && !payload.schema_meta && (
              <>
                <div className="preview-label">Auszug/Statement</div>
                <div className="preview-value">{payload.statement}</div>
              </>
            )}

            {(() => {
              // Zeige nur IBANs, die das Backend als DB‑Treffer geliefert hat
              const dbList = Array.isArray(payload.db_matched_ibans)
                ? payload.db_matched_ibans
                : (payload.matched_iban ? [payload.matched_iban] : []);
              const ibansClean = extractIbans(dbList);
              if (!ibansClean || ibansClean.length === 0) return null;
              return (
                <>
                  <div className="preview-label">IBANs</div>
                  <div>
                    {ibansClean.map((ib, idx) => (
                      <code key={idx} style={{ marginRight: 8 }}>{ib}</code>
                    ))}
                  </div>
                </>
              );
            })()}

            {payload.account_suffix && (
              <>
                <div className="preview-label">Suffix</div>
                <div className="preview-value">{payload.account_suffix}</div>
              </>
            )}
          </div>

          {/* Matched Keywords */}
          {Array.isArray(payload.matched_keywords) && payload.matched_keywords.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="preview-label" style={{ marginBottom: 4 }}>Treffer-Keywords</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {payload.matched_keywords.map((kw, i) => (
                  <span key={i} className="keyword-badge">
                    {typeof kw === "object" ? kw.value : kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Regel-Treffer (aus Backend) */}

          {/* Warnungen */}
          {Array.isArray(payload.warnings) && payload.warnings.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="preview-label" style={{ marginBottom: 4 }}>Warnungen</div>
              <ul style={{ margin: 0 }} className="preview-value">
                {payload.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {/* === SINGLE OCR VIEWER MOUNT START === */}
      {showOCR && ocrRaw ? (
        <OcrOverlayViewer
          file={file}
          ocrRaw={ocrRaw}
          evidenceRaw={evidenceRaw}
          allowedEvidenceKinds={payload?.schema_meta?.evidence_kinds || null}
          page={ocrPage}
          onPageChange={(p) => setOcrPage(p)}
          zoom={ocrZoom}
          onZoomChange={(z) => setOcrZoom(z)}
          showGrid={ocrShowGrid}
          onToggleGrid={() => setOcrShowGrid((v) => !v)}
          showBoxes={ocrShowBoxes}
          onToggleBoxes={() => setOcrShowBoxes((v) => !v)}
          confMin={ocrConfMin}
          onConfMinChange={(v) => setOcrConfMin(v)}
          onClose={() => setShowOCR(false)}
        />
      ) : null}
      {/* === SINGLE OCR VIEWER MOUNT END === */}

      {/* Volltext-Anzeige (falls vom Backend mitgeliefert) */}
      {showText && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Gesamter Text</h3>
          {fullText ? (
            <textarea
              value={fullText}
              readOnly
              style={{ width: "100%", height: "50vh", whiteSpace: "pre", fontFamily: "monospace" }}
            />
          ) : (
            <p style={{ opacity: 0.75 }}>
              Kein Volltext in der Antwort gefunden. (Das Backend kann optional ein Feld
              <code> full_text </code> mitsenden.)
            </p>
          )}
        </div>
      )}
      {showBatchModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999
        }}>
          <div style={{ background: "#fff", borderRadius: 8, padding: 16, width: 640, maxWidth: "95%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h2 style={{ margin: 0 }}>Batch – Massenverarbeitung</h2>
              <button onClick={closeBatchModal} disabled={batchRunning}>
                Schließen
              </button>
            </div>

            {batchError && (
              <div style={{ marginBottom: 8, color: "#b00020", fontWeight: 600 }}>Fehler: {batchError}</div>
            )}

            {browserModeActive && (
              <div style={{
                marginBottom: 8,
                padding: '6px 10px',
                border: '1px solid #c8e6c9',
                background: '#e8f5e9',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600
              }}>
                Browser‑Modus aktiv: Dateien werden lokal aus dem gewählten Quellordner gelesen und in den Ziel‑/Fehlerordner **kopiert**.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 8, marginBottom: 12 }}>
              <label style={{ alignSelf: "center", fontWeight: 600 }}>Quellordner (PDFs)</label>
              <input
                type="text"
                placeholder="/Pfad/zur/Quelle"
                value={batchSourceDir}
                onChange={(e) => setBatchSourceDir(e.target.value)}
                disabled={batchRunning}
              />
              <button type="button" onClick={() => pickDirectory("source")} disabled={batchRunning}>Ordner wählen…</button>

              <label style={{ alignSelf: "center", fontWeight: 600 }}>Zielordner (umbenannt)</label>
              <input
                type="text"
                placeholder="/Pfad/zum/Ziel (Milestone 2)"
                value={batchTargetDir}
                onChange={(e) => setBatchTargetDir(e.target.value)}
                disabled={batchRunning}
              />
              <button type="button" onClick={() => pickDirectory("target")} disabled={batchRunning}>Ordner wählen…</button>

              <label style={{ alignSelf: "center", fontWeight: 600 }}>Fehlerordner</label>
              <input
                type="text"
                placeholder="/Pfad/fuer/Fehler (Milestone 2)"
                value={batchErrorDir}
                onChange={(e) => setBatchErrorDir(e.target.value)}
                disabled={batchRunning}
              />
              <button type="button" onClick={() => pickDirectory("error")} disabled={batchRunning}>Ordner wählen…</button>
            </div>

            <div style={{ fontSize: 12, opacity: 0.75, marginTop: -6, marginBottom: 8 }}>
              Tipp: Der Browser-Picker zeigt nur den Ordnernamen. Für die serverseitige Verarbeitung bitte den
              <em> absoluten Systempfad </em> in die Felder eintragen (z. B. /Users/…/Eingang). Falls dein Browser
              den Ordner-Picker nicht unterstützt, nutze den Fallback.
            </div>

            {/* Fallback: versteckte Directory-Inputs (Chrome/Edge/Safari: webkitdirectory) */}
            <input
              ref={sourcePickerRef}
              type="file"
              style={{ display: "none" }}
              webkitdirectory="true"
              directory="true"
              mozdirectory="true"
              onChange={(e) => onDirInputChange("source", e)}
            />
            <input
              ref={targetPickerRef}
              type="file"
              style={{ display: "none" }}
              webkitdirectory="true"
              directory="true"
              mozdirectory="true"
              onChange={(e) => onDirInputChange("target", e)}
            />
            <input
              ref={errorPickerRef}
              type="file"
              style={{ display: "none" }}
              webkitdirectory="true"
              directory="true"
              mozdirectory="true"
              onChange={(e) => onDirInputChange("error", e)}
            />

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
              {!batchRunning ? (
                <button
                  onClick={startBatch}
                  disabled={
                    browserModeActive ? !(batchSourceHandle && batchTargetHandle && batchErrorHandle) : !batchSourceDir
                  }
                >
                  Start
                </button>
              ) : (
                <button onClick={stopBatch}>Stop</button>
              )}
              <div style={{ marginLeft: 8, fontSize: 13, opacity: 0.8 }}>
                {batchRunning ? "Läuft …" : (batchId ? "Beendet" : "Bereit")}
              </div>
            </div>

            <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 10, background: "#fafafa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <div>
                  Fortschritt: {batchDone} / {batchTotal} ({batchOk} ok, {batchErr} Fehler)
                </div>
                <div style={{ fontFamily: "monospace" }}>{batchCurrent || "–"}</div>
              </div>
              <div style={{ height: 8, background: "#eee", borderRadius: 4, overflow: "hidden", marginBottom: 8 }}>
                <div
                  style={{
                    height: 8,
                    width: `${batchTotal > 0 ? Math.round((batchDone / batchTotal) * 100) : 0}%`,
                    background: "#4caf50"
                  }}
                />
              </div>
              <div style={{ maxHeight: 240, overflow: "auto", fontFamily: "monospace", fontSize: 12, background: "#fff", padding: 8, border: "1px solid #eee", borderRadius: 4 }}>
                {Array.isArray(batchLog) && batchLog.length > 0 ? (
                  batchLog.map((ln, idx) => (
                    <div key={idx} style={{ whiteSpace: "pre-wrap" }}>{ln}</div>
                  ))
                ) : (
                  <div style={{ opacity: 0.6 }}>Noch keine Log-Zeilen…</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}